import axios from 'axios';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { Component, createRef } from 'react';
import { withRouter } from 'react-router';
import { PitstopButton, PitstopModal } from 'shared';
import styled from 'styled-components';
import dayjs from 'dayjs';
import * as Sentry from '@sentry/react';

import {
  Button,
  Checkbox,
  Col,
  Collapse,
  Input,
  InputNumber,
  Modal,
  notification,
  Row,
  Select,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import _, {
  deburr,
  first,
  initial,
  isArray,
  isEmpty,
  kebabCase,
  throttle,
} from 'lodash';
import moment from 'moment';
import {
  AppStore,
  CarStore,
  ContactStore,
  CurrentUserStore,
  IssueStore,
  PurchaseOrderStore,
  ShopStore,
  TableStore,
  UserStore,
  WorkOrderStore,
  InspectionsStore,
} from 'stores';

import WorkOrderForm from 'components/WorkOrder/WODetailsForm';
import { customFleets } from 'shared';
// import WorkOrderIssuesSummary from 'components/WorkOrder/WOIssuesSummary';
import DealershipChooser from 'components/DealershipChooser';
import WorkOrderLineItems from 'components/WorkOrder/LineItems/WorkOrderLineItems';
import WorkOrderCostSummary from 'components/WorkOrder/WorkOrderCostSummary';

import LineItems from 'components/WorkOrder/VMRS/LineItems';
import { v4 as uuidv4 } from 'uuid';

import {
  DownloadOutlined,
  PrinterOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import FlexRow from 'components/General/FlexRow';
import MediaPreview from 'components/General/MediaPreview';
import NotificationBanner from 'components/NotificationBanner';
import mediaErrorTracker from '../../services/mediaErrorTracking';
import PurchaseOrderAddModal from 'components/PurchaseOrders/PurchaseOrderAddModal';
import { computed, makeObservable, observable } from 'mobx';
import { Link } from 'react-router-dom';
import { webServiceProvider } from 'shared';
import { checkIfMobileScreenSize, getScreenSize } from 'shared/utils';
import { PitstopTableCacheStore } from 'stores/CacheStore';
import { Logger } from 'stores/Classes';
import WorkOrderOptionalInformation from '../../components/WorkOrder/WorkOrderOptionalInformation';
import UploadImageModal from './modals/UploadImageModal';
import { WorkOrderProvider } from './WorkOrderDetailsContext';
import { EventBus } from 'stores/EventBus';

const { Text } = Typography;
const { TextArea } = Input;
const { Panel } = Collapse;

const ImagePreviewModal = styled(Modal)`
  &&& {
    height: 100%;
    width: 70vw !important;

    @media (max-width: 768px) {
      width: 100vw !important;
    }
  }

  .ant-modal-content {
    height: 100%;
    width: 100%;
  }

  .ant-modal-body {
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const StyledModal = styled(PitstopModal)`
  && {
    .ant-modal-header {
      margin-bottom: 30px;
      .ant-modal-title {
        color: #02103d;
      }
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 25px;
`;

const Container = styled.div`
  padding-right: 2vw;
`;

const ActionButtonsWrapper = styled.div`
  right: 15px;

  button {
    margin-left: 10px;
  }

  @media screen and (max-width: 1024px) {
    .small-screen {
      font-size: 13px;
    }
  }
`;

const ActionButtonsWrapperFlexEnd = styled(ActionButtonsWrapper)`
  display: flex;
  justify-content: flex-end;
`;

const UploadButton = styled(Button)`
  background-color: #fff;
  border: 1px solid #1890ff;
  color: #1890ff;
  &:hover {
    background-color: #ecf6fd;
    border-color: #1890ff;
  }
  &:focus,
  &:active {
    background-color: #fff;
    border-color: #1890ff;
  }
`;

const ImportedInformation = styled.div`
  background-color: #f0f0f0;
  padding: 1rem;
  margin-top: 1rem;
  border-radius: 4px;
  p {
    margin: 0;
  }
`;

const ADD_PART_MODAL = 'ADD_PART_MODAL';

class WorkOrderDetails extends Component {
  static propTypes = {
    history: PropTypes.object.isRequired,
    match: PropTypes.object,
    isSlideOutMode: PropTypes.bool,
    initialWorkOrderData: PropTypes.object,
  };

  constructor(props) {
    super(props);
    makeObservable(this, {
      shopId: observable,
      shopUsers: computed,
    });

    this.hasUnsavedChangesRef = createRef();
    this.hasUnsavedChangesRef.current = false;

    this.fetchBase64FileAndRender = this.fetchBase64FileAndRender.bind(this);
    // Add router prompt for unsaved changes
    this.unblock = props.history.block(this.historyBlock);

    // Create throttled version of handleScroll to improve performance
    this.throttledHandleScroll = throttle(this.handleScroll, 50);

    // Create throttled version of resize handler for MediaPreview recalculation
    this.throttledHandleResizeForMediaPreview = throttle(() => {
      // Set recalculating flag immediately to prevent scroll from using stale values
      this.isRecalculatingMediaPreviewPosition = true;

      // Clear cached positions to force recalculation on next scroll
      this.originalMediaPreviewTopDistance = 0;
      this.originalMediaPreviewWidth = 0;
      this.originalMediaPreviewLeft = 0;

      if (this.state && this.state.scannedWorkOrderPreview) {
        this.recalculateMediaPreviewPosition(100); // Shorter delay for resize
      } else {
        // If no preview, clear the flag
        this.isRecalculatingMediaPreviewPosition = false;
      }
    }, 300);
  }

  handleSlideOutClose = () => {
    if (this.state.hasUnsavedChanges) {
      const modal = Modal.confirm({
        title: 'Save Changes',
        content: 'Save any changes to the work order to retain your updates.',
        okText: 'Save',
        cancelText: 'Exit Without Saving',
        closable: true,
        maskClosable: false,
        // Override the cancel button onClick to set our flag
        footer: () => (
          <>
            <Button
              type="primary"
              onClick={() => {
                modal.destroy();
                EventBus.$emit('dashboardWrapper/CLOSE_SLIDE_OUT');
              }}
            >
              Exit Without Saving
            </Button>
            <Button
              onClick={() => {
                this.onClickSave('exit');
                modal.destroy();
              }}
            >
              Save & Exit
            </Button>
          </>
        ),
      });
    } else {
      EventBus.$emit('dashboardWrapper/CLOSE_SLIDE_OUT');
    }
  };

  historyBlock = (location) => {
    if (
      (this.hasUnsavedChangesRef?.current || this.state.hasUnsavedChanges) &&
      !this.props.isSlideOutMode
    ) {
      const modal = Modal.confirm({
        title: 'Save Changes',
        content: 'Save any changes to the work order to retain your updates.',
        okText: 'Save & Exit',
        cancelText: 'Exit Without Saving',
        closable: true,
        maskClosable: false,
        // Override the cancel button onClick to set our flag
        footer: () => (
          <>
            <Button
              type="primary"
              onClick={() => {
                modal.destroy();
                this.unblock();
                this.props.history.push(location.pathname);
                // hide the add part modal
                AppStore.openModals.set(ADD_PART_MODAL, false);
              }}
            >
              Exit Without Saving
            </Button>
            <Button
              onClick={() => {
                this.onClickSave('exit', location.pathname);
                modal.destroy();
              }}
            >
              Save & Exit
            </Button>
          </>
        ),
      });
      return false;
    }
    return true;
  };

  currentDateTime = moment();
  randomWorkOrderId = () => _.padStart(_.random(999999), '0');

  usersTableStore = new TableStore();

  shopId = ShopStore.currentShop.id;

  get shopUsers() {
    const users = _.map(this.usersTableStore.data, (id) =>
      UserStore.data.get(id)
    );
    return users;
  }

  getInitialState = () => ({
    isEdit: false,
    isGeneratingWorkOrderInvoiceNumber: true,
    loadingAssetDefects: false,
    assetDefects: [],
    activeTab: 'work-order-details',
    lineItems: [
      {
        sys: '',
        asy: '',
        comp: '',
        header: 'New Work Order Line 0',
        key: uuidv4(),
        title: 'New Work Order Line 0',
        description: '',
        laborTotal: 0,
        partsTotal: 0,
        miscTotal: 0,
        laborTotalChangedManually: false,
        partsTotalChangedManually: false,
        miscTotalChangedManually: false,
        labours: [],
        parts: [],
        services: [],
        miscellaneous: [],
        defects: [],
        pms: [],
        expanded: true,
        active: true,
        diagnosis: {
          complaintId: null,
          causeId: null,
          correctionId: null,
          ocrImported: false,
        },
        vmrs_full_code: null,
        vmrs_full_description: '',
        vmrs_sys_code: null,
        vmrs_sys_text: '',
        vmrs_asm_code: null,
        vmrs_asm_text: '',
        vmrs_comp_code: null,
        vmrs_full_code_ai: false,
      },
    ],
    purchaseOrders: [],
    addPOModalVisible: false,
    costsTotalHasManualOverride: false,
    laboursTotalHasManualOverride: false,
    partsTotalHasManualOverride: false,
    isImagePreviewModalVisible: false,
    currentImagePreviewSrc: '',
    manualOverrides: {
      manualLaboursTotal: 0,
      manualPartsTotal: 0,
      manualCostsTotal: 0,
    },
    currentCarIssues: [],
    selectedCarId: undefined,
    savingWorkOrder: false,
    currentWorkOrder: null,
    loadingWorkOrder: true,
    loadingWorkOrderLines: true,
    loadingInitial: true,
    title: '',
    hasUnsavedChanges: false,
    initialFormValues: {
      woInvoiceNumber: '',
      invoiceNumberCustomer: '',
      woCreatedBy: '',
      woSelectVehicle: undefined,
      woAssetType: checkIfMobileScreenSize() ? 'Trailer' : 'Vehicle',
      woAssignedTo: '',
      woStatus: '',
      woRepairType: 'driver_identified',
      woRepairPriority: 'minor',
      woIssueReportDate: this.currentDateTime,
      purchase_order_id: undefined,
      additionalNotes: '',
      driverReportDetails: {
        driverName: '',
        driverNumber: '',
        location: '',
        timeOfEvent: null,
        weightOfVehicle: '',
        driverNotes: '',
        billingThrough: null,
      },
      woStartDate: undefined,
      woCompletedDate: undefined,
      mileage: undefined,
      engineHours: undefined,
      locationCode: undefined,
      tax: {
        type: 'percentage',
        value: 0,
      },
      secondTax: {
        type: 'percentage',
        value: 0,
      },
      misc: {
        type: 'absolute',
        value: 0,
      },
      warrantySavings: 0,
      woSource: '',
      rawData: '',
      laborsTotal: 0,
      partsTotal: 0,
      costsTotal: 0,
      laborTotalChangedManually: false,
      partsTotalChangedManually: false,
      miscTotalChangedManually: false,
      vendor_id: undefined,
    },
    shopUsers: [],
    shopParts: [],
    shopVendors: [],
    loadingVendors: false,
    carDetails: {},
    newPart: {
      name: '',
      description: '',
      number: '',
      cost: 0,
    },
    loadingSavingNewPart: false,
    currentLinePartIsBeingAdded: null,
    currentUploadingFile: null,
    scanQueue: [],
    uploadedFiles: [],
    uploadedImages: [],
    wo_images: [],
    wo_documents: [],
    showUploadButton: true,
    isUploadWorkOrderModalVisible: false,
    isLoadingWorkOrderUpload: false,
    isErrorWorkOrderUpload: false,
    importedInformation: null,
    scannedWorkOrderPreview: null,
    previewImageUrls: [],
    currentPreviewIndex: 0,
    isTiff: false,
    loadingPreview: false,
    isDisabled: false,
    uploading: false,
    isMediaPreviewFixed: false,
    // progress: 0,
    currentStep: 0,
    scanDone: false,
    isUploadCancelled: false,
    isMobileScreenSize: checkIfMobileScreenSize(),
    isLabourWarning: false,
    isPartsWarning: false,
    isPartsCostDiscrepancy: false,
    isLaborCostDiscrepancy: false,
    screenSize: getScreenSize(),
    workOrderImgOcrSignal: null,
    workOrderFormData: {},
    unsavedFilesInitialized: false,
    loadingCarData: false,
    isShopDataReady: false,
  });

  state = this.getInitialState();

  workOrderFormRef = createRef();
  lineItemsRef = createRef();
  costSummaryRef = createRef();
  imageUploaderContainerRef = {};
  documentUploaderContainerRef = {};

  setImageUploaderContainerRef = (ref) => {
    this.imageUploaderContainerRef = ref;
  };

  setDocumentUploaderContainerRef = (ref) => {
    this.documentUploaderContainerRef = ref;
  };

  orderedSections = [
    'work-order-details',
    'work-order-line-items',
    'optional-information',
    'summary',
  ];

  forceUpdateTabs() {
    this.forceUpdate(); // Force a re-render of the component
  }

  scrollContainer;

  componentDidUpdate(prevProps) {
    // Check if work order ID has changed and Reload if ID has changed
    const prevId =
      prevProps.isSlideOutMode && prevProps.initialWorkOrderData
        ? prevProps.initialWorkOrderData.id
        : prevProps.match?.params?.id;

    const currentId =
      this.props.isSlideOutMode && this.props.initialWorkOrderData
        ? this.props.initialWorkOrderData.id
        : this.props.match?.params?.id;

    if (prevId !== currentId && currentId) {
      // Reset state to initial values and reload work order
      this.setState(this.getInitialState(), () => {
        this.load();
      });
      return;
    }

    const form = this.workOrderFormRef.form || this.workOrderFormRef;

    if (form.current === null) {
      console.log('!!! componentDidUpdate() form: NO FORM YET!');
    } else {
      if (this.props.location.state?.data?.formId) {
        form.setFieldsValue({
          woCreatedBy: this.props.location.state?.data?.createdBy,
          woSelectVehicle: this.props.location.state?.data?.asset,
          woAssetType: this.props.location.state?.data?.assetType,
        });

        const newFormId = this.props.location.state?.data?.formId;
        if (newFormId && newFormId !== this.state.inspectionFormId) {
          this.setState((prevState) => {
            const lineItem = {
              ...prevState.lineItems[0],
              inspection_issues: this.props.location.state?.data?.issues,
            };

            return {
              ...prevState,
              inspectionFormId: newFormId,
              inspectionFormTitle: this.props.location.state?.data?.formTitle,
              inspectionIssues: this.props.location.state?.data?.issues,
              lineItems: [lineItem],
            };
          });
        }
      }
    }
    const pushedData = this.props.location.state?.data;
    if (
      pushedData &&
      !this.state.unsavedFilesInitialized &&
      this.documentUploaderContainerRef.setFileList &&
      this.imageUploaderContainerRef.setFileList
    ) {
      setTimeout(() => {
        const uuidPattern = new RegExp(
          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
        );

        // Handle documents - use preserved files if available, otherwise use existing logic
        let documentFileList = [];
        if (
          pushedData.preservedDocumentFiles &&
          pushedData.preservedDocumentFiles.length > 0
        ) {
          // Use preserved File objects and recreate blob URLs
          documentFileList = pushedData.preservedDocumentFiles.map(
            (file, index) => {
              const blobUrl = URL.createObjectURL(file);
              return {
                uid: `doc-${index}`,
                name: file.name,
                status: 'done',
                url: blobUrl,
                type: file.type,
                size: file.size,
              };
            }
          );
        } else {
          // Fallback to existing logic
          documentFileList = [
            ...(this.state.uploadedFiles || []).map((file) => {
              let url;
              try {
                url = URL.createObjectURL(file.file);
              } catch (error) {
                url = file.file.url;
              }
              return {
                uid: file.file.uid,
                name: file.file.name,
                status: 'done',
                url,
                type: file.file.type,
                size: file.file.size,
              };
            }),
            ...(this.state.wo_documents || []).map((url) => ({
              url,
              status: 'done',
              name: url.split('/').pop().replace(uuidPattern, ''),
            })),
          ];
        }

        // Handle images - use preserved files if available, otherwise use existing logic
        let imageFileList = [];
        if (
          pushedData.preservedImageFiles &&
          pushedData.preservedImageFiles.length > 0
        ) {
          // Use preserved File objects and recreate blob URLs
          imageFileList = pushedData.preservedImageFiles.map((file, index) => {
            const blobUrl = URL.createObjectURL(file);
            return {
              uid: `img-${index}`,
              name: file.name,
              status: 'done',
              url: blobUrl,
              thumbUrl: blobUrl,
              type: file.type,
              size: file.size,
            };
          });
        } else {
          // Fallback to existing logic
          imageFileList = [
            ...(this.state.uploadedImages || []).map((img) => img.file),
            ...(this.state.wo_images || []).map((url) => ({
              url,
              status: 'done',
              thumbUrl: url,
            })),
          ];
        }

        this.documentUploaderContainerRef.setFileList(documentFileList);
        this.imageUploaderContainerRef.setFileList(imageFileList);
      }, 100);
      this.setState({ unsavedFilesInitialized: true });
    }
  }

  async componentDidMount() {
    // Only set sidebar selection if not in slide-out mode
    if (!this.props.isSlideOutMode) {
      AppStore.setSelectedSidebarKey('/work-order');
    } else {
      EventBus.$on(
        'dashboardWrapper/CHECK_UNSAVED_BEFORE_CLOSE',
        this.handleSlideOutClose
      );

      EventBus.$on(
        'workOrders:updateExpandedWorkOrder',
        this.handleWorkOrderUpdate
      );
    }

    // Listen for sidebar toggle events
    EventBus.$on('dashboardWrapper/SIDEBAR_TOGGLED', this.handleSidebarToggle);

    if (this.props.onInstanceReady) {
      this.props.onInstanceReady(this);
    }

    const pushedData = this.props.location.state?.data;
    if (pushedData) {
      // Set form values that aren't handled by componentDidUpdate
      this.setState((prevState) => ({
        initialFormValues: {
          ...prevState.initialFormValues,
          ...pushedData?.formData,
          tax: pushedData?.tax || prevState.initialFormValues.tax,
          secondTax:
            pushedData?.secondTax || prevState.initialFormValues.secondTax,
          misc: pushedData?.misc || prevState.initialFormValues.misc,
          warrantySavings:
            pushedData?.warrantySavings ||
            prevState.initialFormValues.warrantySavings,
          driverReportDetails:
            pushedData?.driverReportDetails ||
            prevState.initialFormValues.driverReportDetails,
          vendor_id:
            pushedData?.vendor_id || prevState.initialFormValues.vendor_id,
          locationCode:
            pushedData?.locationCode ||
            prevState.initialFormValues.locationCode,
        },
        manualOverrides: {
          ...prevState.manualOverrides,
          ...pushedData.manualOverrides,
        },
        costsTotalHasManualOverride:
          pushedData.costsTotalHasManualOverride ||
          prevState.costsTotalHasManualOverride,
        laboursTotalHasManualOverride:
          pushedData.laboursTotalHasManualOverride ||
          prevState.laboursTotalHasManualOverride,
        partsTotalHasManualOverride:
          pushedData.partsTotalHasManualOverride ||
          prevState.partsTotalHasManualOverride,
        hasUnsavedChanges: pushedData.hasUnsavedChanges || false,
      }));

      if (this.hasUnsavedChangesRef?.current !== undefined) {
        this.hasUnsavedChangesRef.current =
          pushedData.hasUnsavedChanges || false;
      }
    }

    await this.load();

    // check if there is a 'tab' query param
    const queryParams = new URLSearchParams(this.props.history.location.search);
    const tab = queryParams.get('tab');
    if (tab) {
      this.setState({ activeTab: tab });
    }

    // on window resize, check if the screen size is mobile
    window.addEventListener('resize', this.handleResize);

    // Add scroll event listener to window with throttled handler
    window.addEventListener('scroll', this.throttledHandleScroll);
  }

  getUnsavedData() {
    // Get the actual File objects from uploadedImages to preserve them across navigation
    const preservedImages = this.state.uploadedImages.map((img) => img.file);
    // Get the actual File objects from uploadedFiles to preserve them across navigation
    const preservedDocuments = this.state.uploadedFiles.map(
      (file) => file.file
    );

    return {
      formData: this.state.workOrderFormData,
      lineItems: this.state.lineItems,
      purchaseOrders: this.state.purchaseOrders,
      uploadedFiles: this.state.uploadedFiles,
      uploadedImages: this.state.uploadedImages,
      wo_images: this.state.wo_images,
      wo_documents: this.state.wo_documents,
      // Store the actual File objects for blob URLs that need to be recreated
      preservedImageFiles: preservedImages,
      preservedDocumentFiles: preservedDocuments,
      currentImagePreviewSrc: this.state.currentImagePreviewSrc,
      previewImageUrls: this.state.previewImageUrls,
      currentCarIssues: this.state.currentCarIssues,
      selectedCarId: this.state.selectedCarId,
      tax: this.state.initialFormValues.tax,
      secondTax: this.state.initialFormValues.secondTax,
      misc: this.state.initialFormValues.misc,
      warrantySavings: this.state.initialFormValues.warrantySavings,
      manualOverrides: this.state.manualOverrides,
      costsTotalHasManualOverride: this.state.costsTotalHasManualOverride,
      laboursTotalHasManualOverride: this.state.laboursTotalHasManualOverride,
      partsTotalHasManualOverride: this.state.partsTotalHasManualOverride,
      driverReportDetails: this.state.initialFormValues.driverReportDetails,
      vendor_id: this.state.initialFormValues.vendor_id,
      locationCode: this.state.initialFormValues.locationCode,
      hasUnsavedChanges: this.state.hasUnsavedChanges,
    };
  }

  // Method to restore unsaved data from navigation state
  restoreUnsavedData(data) {
    if (data) {
      // Restore preserved File objects and recreate blob URLs for images
      if (data.preservedImageFiles && data.preservedImageFiles.length > 0) {
        // Recreate blob URLs for the preserved File objects
        const recreatedImages = data.preservedImageFiles.map((file) => {
          const blobUrl = URL.createObjectURL(file);
          return {
            file,
            additionalData: null,
            url: blobUrl,
          };
        });

        // Update wo_images with the recreated blob URLs
        const recreatedWoImages = recreatedImages.map((img) => img.url);

        this.setState({
          uploadedImages: recreatedImages,
          wo_images: recreatedWoImages,
        });
      } else {
        // Fallback to original wo_images if no preserved files
        if (data.wo_images && data.wo_images.length > 0) {
          this.setState({ wo_images: data.wo_images });
        }
      }

      // Restore preserved File objects and recreate blob URLs for documents
      if (
        data.preservedDocumentFiles &&
        data.preservedDocumentFiles.length > 0
      ) {
        // Recreate blob URLs for the preserved File objects
        const recreatedDocuments = data.preservedDocumentFiles.map((file) => {
          const blobUrl = URL.createObjectURL(file);
          return {
            file,
            additionalData: null,
            url: blobUrl,
          };
        });

        // Update wo_documents with the recreated blob URLs
        const recreatedWoDocuments = recreatedDocuments.map((doc) => doc.url);

        this.setState({
          uploadedFiles: recreatedDocuments,
          wo_documents: recreatedWoDocuments,
        });
      } else {
        // Fallback to original wo_documents if no preserved files
        if (data.wo_documents && data.wo_documents.length > 0) {
          this.setState({ wo_documents: data.wo_documents });
        }
      }

      // Restore other form data
      if (data.formData) {
        this.setState({ workOrderFormData: data.formData });
      }

      if (data.lineItems) {
        this.setState({ lineItems: data.lineItems });
      }

      if (data.purchaseOrders) {
        this.setState({ purchaseOrders: data.purchaseOrders });
      }

      if (data.uploadedFiles && !data.preservedDocumentFiles) {
        this.setState({ uploadedFiles: data.uploadedFiles });
      }

      if (data.uploadedImages && !data.preservedImageFiles) {
        this.setState({ uploadedImages: data.uploadedImages });
      }

      if (data.selectedCarId) {
        this.setState({ selectedCarId: data.selectedCarId });
      }

      // Mark as having unsaved changes if data was restored
      if (
        data.wo_images ||
        data.wo_documents ||
        data.formData ||
        data.preservedImageFiles ||
        data.preservedDocumentFiles
      ) {
        this.setState({ hasUnsavedChanges: true });
      }
    }
  }

  // Method to refresh thumbnail previews
  refreshThumbnailPreviews() {
    // Force re-render of image uploader components
    if (
      this.imageUploaderContainerRef &&
      this.imageUploaderContainerRef.setFileList
    ) {
      // Update the file list to trigger re-render
      const currentImages = this.state.wo_images.map((url, index) => ({
        uid: `old-${index}`,
        name: `Image ${index + 1}`,
        status: 'done',
        url,
        thumbUrl: url,
      }));

      this.imageUploaderContainerRef.setFileList(currentImages);
    }

    // Force re-render of document uploader components
    if (
      this.documentUploaderContainerRef &&
      this.documentUploaderContainerRef.setFileList
    ) {
      const currentDocuments = this.state.wo_documents.map((url, index) => ({
        uid: `doc-${index}`,
        name: `Document ${index + 1}`,
        status: 'done',
        url,
        thumbUrl: url,
      }));

      this.documentUploaderContainerRef.setFileList(currentDocuments);
    }

    // Trigger a small state update to force re-render
    this.setState({
      thumbnailRefreshTrigger: Date.now(),
    });
  }

  s3UriToHttpUrl = (s3Uri) => {
    if (!s3Uri) {
      console.error('S3 URI is null or undefined');
      return null;
    }

    if (!s3Uri.startsWith('s3://')) {
      console.error('Invalid S3 URI format');
      return null;
    }

    const bucketName = 'work-orders-scan';
    const region = 'us-east-2';
    const objectKey = s3Uri.replace(`s3://${bucketName}/`, '');

    if (!objectKey) {
      console.error('S3 URI does not contain an object key');
      return null;
    }

    return `https://${bucketName}.s3.${region}.amazonaws.com/${objectKey}`;
  };

  fetchBase64FileAndRender = async (url, type = 'png') => {
    const workOrderId = this.state.workOrder?.id?.toString();

    this.setState({ loadingPreview: true });

    const cleanUrl = url
      .replace('temp_ufp_', '')
      .replace('_base64.txt', '.' + type);

    const filename = cleanUrl.split('/').pop();

    const workOrder = this.state.workOrder;
    let foundUrl = null;

    if (workOrder?.wo_files) {
      if (Array.isArray(workOrder.wo_files.wo_documents)) {
        foundUrl = workOrder.wo_files.wo_documents.find(
          (docUrl) => docUrl && docUrl.includes(filename)
        );
      }

      if (!foundUrl && Array.isArray(workOrder.wo_files.wo_images)) {
        foundUrl = workOrder.wo_files.wo_images.find(
          (imgUrl) => imgUrl && imgUrl.includes(filename)
        );
      }
    }

    if (foundUrl) {
      this.onClickImage(foundUrl);
      this.setState({ loadingPreview: false });
      return;
    }

    const possiblePaths = [
      cleanUrl, // /filename.type
      cleanUrl.replace(
        'amazonaws.com/',
        'amazonaws.com/UFP-Workorder/production/'
      ), // /production/filename.type
      cleanUrl.replace(
        'amazonaws.com/',
        'amazonaws.com/UFP-Workorder/staging/'
      ), // /staging/filename.type
    ];

    for (const testUrl of possiblePaths) {
      try {
        const response = await fetch(testUrl, {
          method: 'HEAD',
          timeout: 5000,
        });

        if (response.ok) {
          this.onClickImage(testUrl);
          this.setState({ loadingPreview: false });
          return;
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: {
            component: 'WorkOrderDetails',
            method: 'fetchBase64FileAndRender',
          },
          extra: {
            workOrderId,
            cleanUrl,
            testUrl,
          },
        });
      }
    }

    this.onClickImage(cleanUrl);
    this.setState({ loadingPreview: false });
  };

  showMultiPageTiffPreview = (base64) => {
    try {
      const arrayBuffer = Uint8Array.from(atob(base64), (char) =>
        char.charCodeAt(0)
      ).buffer;
      const tiff = new window.Tiff({ buffer: arrayBuffer });
      const pageCount = tiff.countDirectory();
      const imageUrls = [];

      for (let i = 0; i < pageCount; i++) {
        tiff.setDirectory(i);
        const canvas = tiff.toCanvas();
        if (canvas) {
          const dataUrl = canvas.toDataURL();
          imageUrls.push(dataUrl);
        }
      }

      if (imageUrls.length > 0) {
        setTimeout(() => {
          this.setState({
            previewImageUrls: imageUrls,
            scannedWorkOrderPreview: imageUrls[0],
            currentPreviewIndex: 0,
            isTiff: true,
            loadingPreview: false,
          });
        }, 100);
      } else {
        console.error('No TIFF pages could be rendered');
        const workOrderId = this.state.workOrder?.id?.toString();

        // Track TIFF rendering failure
        mediaErrorTracker.trackMediaError({
          workOrderId,
          mediaUrl: 'tiff-data',
          mediaType: 'tiff',
          errorType: 'format',
          errorMessage:
            'No TIFF pages could be rendered - corrupt or unsupported TIFF format',
        });

        this.setState({
          scannedWorkOrderPreview: null,
          isTiff: false,
          loadingPreview: false,
        });
      }
    } catch (error) {
      console.error('Error processing TIFF image:', error);
      const workOrderId = this.state.workOrder?.id?.toString();

      // Track TIFF processing error
      mediaErrorTracker.trackMediaError({
        workOrderId,
        mediaUrl: 'tiff-data',
        mediaType: 'tiff',
        errorType: 'format',
        errorMessage: `TIFF processing error: ${error.message}`,
      });

      this.setState({
        scannedWorkOrderPreview: null,
        isTiff: false,
        loadingPreview: false,
      });
    }
  };

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.throttledHandleScroll);

    // Clean up media error tracking
    mediaErrorTracker.clearAllTracking();

    // Cancel any pending throttled calls
    if (this.throttledHandleScroll && this.throttledHandleScroll.cancel) {
      this.throttledHandleScroll.cancel();
    }
    if (
      this.throttledHandleResizeForMediaPreview &&
      this.throttledHandleResizeForMediaPreview.cancel
    ) {
      this.throttledHandleResizeForMediaPreview.cancel();
    }
    if (this.unblock) {
      this.unblock();
    }
    if (this.props.isSlideOutMode) {
      EventBus.$off(
        'dashboardWrapper/CHECK_UNSAVED_BEFORE_CLOSE',
        this.handleSlideOutClose
      );
      EventBus.$off(
        'workOrders:updateExpandedWorkOrder',
        this.handleWorkOrderUpdate
      );
    }

    // Remove sidebar toggle listener
    EventBus.$off('dashboardWrapper/SIDEBAR_TOGGLED', this.handleSidebarToggle);

    if (this.props.onInstanceReady) {
      this.props.onInstanceReady(null);
    }
  }

  handleWorkOrderUpdate = (updatedWorkOrder) => {
    if (
      this.state.currentWorkOrder &&
      updatedWorkOrder &&
      this.state.currentWorkOrder.id === updatedWorkOrder.id
    ) {
      this.setState({
        currentWorkOrder: updatedWorkOrder,
      });

      EventBus.$emit('dashboardWrapper/UPDATE_SLIDE_OUT', {
        type: 'work-order',
        data: updatedWorkOrder,
        title: `Work Order #${updatedWorkOrder.invoice_number || updatedWorkOrder.id}`,
      });
    }
  };

  handleResize = () => {
    this.setState({
      isMobileScreenSize: checkIfMobileScreenSize(),
      screenSize: getScreenSize(),
    });

    // Always recalculate MediaPreview position on resize if it exists
    // This handles zoom changes and any other layout adjustments
    if (this.state.scannedWorkOrderPreview) {
      this.throttledHandleResizeForMediaPreview();
    }
  };

  recalculateMediaPreviewPosition = (delay = 350) => {
    // Set flag to prevent scroll from fixing during recalculation
    this.isRecalculatingMediaPreviewPosition = true;

    // Wait for any animations to complete
    setTimeout(() => {
      const mediaPreview = document.getElementById('media-preview');
      if (!mediaPreview) {
        this.isRecalculatingMediaPreviewPosition = false;
        return;
      }

      // If MediaPreview is fixed, we need to temporarily unfix it to get correct measurements
      if (this.state.isMediaPreviewFixed) {
        // Temporarily set to not fixed to get accurate measurements
        this.setState({ isMediaPreviewFixed: false }, () => {
          // Small delay to ensure DOM has updated
          setTimeout(() => {
            const mediaPreviewElement =
              document.getElementById('media-preview');
            if (!mediaPreviewElement) {
              this.isRecalculatingMediaPreviewPosition = false;
              return;
            }

            // Get the parent column element
            const parentCol = mediaPreviewElement.closest('.ant-col');
            if (parentCol) {
              const parentRect = parentCol.getBoundingClientRect();
              const mediaRect = mediaPreviewElement.getBoundingClientRect();
              const scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;

              // Update all position data with new measurements
              this.originalMediaPreviewLeft = parentRect.left;
              this.originalMediaPreviewWidth = mediaPreviewElement.offsetWidth;
              this.originalMediaPreviewTopDistance = mediaRect.top + scrollTop;

              // Re-enable fixed state
              this.setState({ isMediaPreviewFixed: true }, () => {
                // Clear flag after state update
                this.isRecalculatingMediaPreviewPosition = false;
              });
            } else {
              this.isRecalculatingMediaPreviewPosition = false;
            }
          }, 50);
        });
      } else {
        // If not fixed, just update the position data for future use
        const parentCol = mediaPreview.closest('.ant-col');
        if (parentCol) {
          const parentRect = parentCol.getBoundingClientRect();
          const mediaRect = mediaPreview.getBoundingClientRect();
          const scrollTop =
            window.pageYOffset || document.documentElement.scrollTop;

          this.originalMediaPreviewLeft = parentRect.left;
          this.originalMediaPreviewWidth = mediaPreview.offsetWidth;
          this.originalMediaPreviewTopDistance = mediaRect.top + scrollTop;
        }
        this.isRecalculatingMediaPreviewPosition = false;
      }
    }, delay);
  };

  handleSidebarToggle = (collapsed) => {
    // If MediaPreview is currently fixed, we need to update its position immediately
    if (this.state.isMediaPreviewFixed && this.state.scannedWorkOrderPreview) {
      // Clear cached positions to force recalculation
      this.originalMediaPreviewTopDistance = 0;
      this.originalMediaPreviewWidth = 0;
      this.originalMediaPreviewLeft = 0;

      // Temporarily unfix to get new measurements, then refix with correct position
      this.recalculateMediaPreviewPosition();
    } else {
      // If not fixed, just clear the cache for future use
      this.originalMediaPreviewTopDistance = 0;
      this.originalMediaPreviewWidth = 0;
      this.originalMediaPreviewLeft = 0;
    }
  };

  originalMediaPreviewTopDistance = 0;
  originalMediaPreviewWidth = 0;
  originalMediaPreviewLeft = 0;
  isRecalculatingMediaPreviewPosition = false;
  scrollToTopAndSaveOriginalMediaPreviewData = () => {
    // Reset fixed state when scrolling to top
    this.setState({ isMediaPreviewFixed: false });

    if (this.scrollContainer) {
      this.scrollContainer.scrollTop = 0;
    }

    // Scroll window to top as well
    window.scrollTo(0, 0);

    // Reset stored positions to force recalculation
    this.originalMediaPreviewTopDistance = 0;
    this.originalMediaPreviewWidth = 0;
    this.originalMediaPreviewLeft = 0;

    // Use multiple timeouts to ensure we capture the dimensions after rendering
    const captureMediaPreviewData = () => {
      const mediaPreview = document.getElementById('media-preview');
      if (
        mediaPreview &&
        mediaPreview.offsetWidth > 0 &&
        !this.state.isMediaPreviewFixed
      ) {
        const rect = mediaPreview.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;

        // Calculate absolute position from top of document
        // This accounts for any current scroll position
        this.originalMediaPreviewTopDistance = rect.top + scrollTop;
        this.originalMediaPreviewWidth = mediaPreview.offsetWidth;

        // Get the parent column element to calculate the correct left position
        const parentCol = mediaPreview.closest('.ant-col');
        if (parentCol) {
          const parentRect = parentCol.getBoundingClientRect();
          this.originalMediaPreviewLeft = parentRect.left;
        } else {
          this.originalMediaPreviewLeft = rect.left;
        }
      }
    };

    // Try multiple times to ensure we get the correct measurements
    // This handles cases where the component might still be rendering
    setTimeout(captureMediaPreviewData, 100);
    setTimeout(captureMediaPreviewData, 300);
    setTimeout(captureMediaPreviewData, 500);
    setTimeout(captureMediaPreviewData, 800);
  };

  resetMediaPreviewPosition = () => {
    // Method to explicitly reset the media preview to its original position
    this.setState({ isMediaPreviewFixed: false });
    this.originalMediaPreviewTopDistance = 0;
    this.originalMediaPreviewWidth = 0;
    this.originalMediaPreviewLeft = 0;
  };

  handleScroll = (event) => {
    // Don't handle scroll if we're in the middle of recalculating positions
    if (this.isRecalculatingMediaPreviewPosition) {
      return;
    }

    // When in slideout mode, disable fixed positioning to prevent overlap with form
    if (this.props.isSlideOutMode) {
      return;
    }

    const mediaPreview = document.getElementById('media-preview');
    if (!mediaPreview) {
      return;
    }

    // If original position hasn't been captured yet, try to capture it
    if (
      !this.originalMediaPreviewTopDistance ||
      this.originalMediaPreviewTopDistance <= 0
    ) {
      // Only capture when not in fixed position to get accurate measurements
      if (!this.state.isMediaPreviewFixed) {
        const rect = mediaPreview.getBoundingClientRect();
        const scrollTop =
          window.pageYOffset || document.documentElement.scrollTop;
        // Calculate the absolute position from the top of the document
        this.originalMediaPreviewTopDistance = rect.top + scrollTop;

        // Get the parent column element to calculate the correct left position
        const parentCol = mediaPreview.closest('.ant-col');
        if (parentCol) {
          const parentRect = parentCol.getBoundingClientRect();
          this.originalMediaPreviewLeft = parentRect.left;
        } else {
          this.originalMediaPreviewLeft = rect.left;
        }

        // Also capture width if not set
        if (
          !this.originalMediaPreviewWidth ||
          this.originalMediaPreviewWidth <= 0
        ) {
          this.originalMediaPreviewWidth = mediaPreview.offsetWidth;
        }
      }
    }

    // Recalculate width if not set or if it seems incorrect
    if (
      !this.originalMediaPreviewWidth ||
      this.originalMediaPreviewWidth <= 0
    ) {
      const currentWidth = mediaPreview.offsetWidth;
      if (currentWidth > 0 && !this.state.isMediaPreviewFixed) {
        this.originalMediaPreviewWidth = currentWidth;
      }
    }

    const scrollY = window.scrollY || window.pageYOffset;
    const threshold = 70; // Header height

    // Determine if media preview should be fixed
    // Add some buffer (5px) to prevent flickering at the exact threshold
    const shouldBeFixed =
      this.originalMediaPreviewTopDistance > 0 &&
      scrollY >= this.originalMediaPreviewTopDistance - threshold - 5;

    // When scrolling up, ensure we remove fixed position when passing the threshold
    const shouldRemoveFixed =
      this.state.isMediaPreviewFixed &&
      scrollY < this.originalMediaPreviewTopDistance - threshold + 5;

    // Only update state if the fixed status actually needs to change
    if (shouldBeFixed && !this.state.isMediaPreviewFixed) {
      // Only fix if we have valid position values (not zero)
      if (
        this.originalMediaPreviewLeft > 0 &&
        this.originalMediaPreviewWidth > 0
      ) {
        this.setState({ isMediaPreviewFixed: true });
      }
    } else if (
      shouldRemoveFixed ||
      (!shouldBeFixed && this.state.isMediaPreviewFixed)
    ) {
      this.setState({ isMediaPreviewFixed: false });
    }
  };

  loadShopUsers = async () => {
    await UserStore.fetchUsersForTable(
      {
        offset: 0,
        limit: 250,
        filter: null,
      },
      this.usersTableStore
    );
  };

  loadShopPurchaseOrders = async () => {
    try {
      if (
        !ShopStore.currentShop.id ||
        ShopStore.currentShop.id === '-1' ||
        ShopStore.currentShop.id === -1
      ) {
        this.setState({ purchaseOrders: [] });
        return;
      }
      const purchaseOrders = await PurchaseOrderStore.fetchShopPurchaseOrders(
        ShopStore.currentShop.id
      );
      this.setState({ purchaseOrders });
    } catch (error) {
      Logger.error(error);
    }
  };

  loadShopParts = async () => {
    try {
      const shopParts = await WorkOrderStore.getShopSpecificParts(
        ShopStore.currentShop.id,
        {
          include_deleted: true,
        }
      );

      this.setState({ shopParts });
    } catch (error) {
      console.log(error.name, error);
      // check if error is abort error
      if (error.name === 'AbortError') {
        return;
      }
      return notification.error({
        message: 'Oops!',
        key: 'listShopParts',
        description: (
          <Container>Sorry, we couldn't fetch your Parts.</Container>
        ),
      });
    }
  };

  loadShopVendors = async () => {
    try {
      if (
        !ShopStore.currentShop.id ||
        ShopStore.currentShop.id === '-1' ||
        ShopStore.currentShop.id === -1
      ) {
        this.setState({ shopVendors: [], loadingVendors: false });
        return;
      }

      // Set loading state
      this.setState({ loadingVendors: true });

      // Invalidate cache to ensure fresh data
      webServiceProvider.invalidateCachePattern('v1/contact');

      const shopVendors = await ContactStore.getShopContacts(
        ShopStore.currentShop.id
      );

      // If vendors are empty, try once more after a short delay
      if (
        (!shopVendors || shopVendors.length === 0) &&
        ShopStore.currentShop.id
      ) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const retryVendors = await ContactStore.getShopContacts(
          ShopStore.currentShop.id
        );
        this.setState({
          shopVendors: retryVendors || [],
          loadingVendors: false,
        });
      } else {
        this.setState({
          shopVendors: shopVendors || [],
          loadingVendors: false,
        });
      }
    } catch (error) {
      Logger.error(error);
      this.setState({ shopVendors: [], loadingVendors: false });
    }
  };

  hydrateLineItem = (lineItem, index) => {
    return {
      ...lineItem,
      title: lineItem.title || `New Work Order Line ${index + 1}`,
      header: lineItem.title || `New Work Order Line ${index + 1}`,
      key: lineItem.id || lineItem.key || index + 1,
      laborTotal: parseFloat(lineItem.labour_cost || 0),
      partsTotal: parseFloat(lineItem.parts_cost || 0),
      miscTotal: parseFloat(lineItem.misc_cost || 0),
      vmrs_asm_code_id: lineItem.vmrs_asm_code_id,
      vmrs_sys_code_id: lineItem.vmrs_sys_code_id,
      parts: lineItem.parts
        ? lineItem.parts.map((p, i) => ({
            ...p,
            originalAmount: parseFloat(p.amount || 0),
            key: parseInt(`${i + 1}${p.id}`),
            name: `Part ${parseInt(`${i + 1}${p.id}`)}`,
            markup: parseFloat(p.markup || 0),
            cost: parseFloat(p.cost || 0),
            volumeunit: p.volumeunit || '',
          }))
        : [],
      labours: lineItem.labours
        ? lineItem.labours.map((p, i) => {
            const obj = {
              ...p,
              key: parseInt(`${i + 1}${p.id}`),
              name: `Labor ${parseInt(`${i + 1}${p.id}`)}`,
              cost: parseFloat(p.cost || 0),
            };

            return Object.fromEntries(
              Object.entries(obj).filter(
                ([_, value]) =>
                  value !== null && value !== undefined && value !== ''
              )
            );
          })
        : [],
      services: lineItem.services
        ? lineItem.services.map((p, i) => ({
            ...p,
            key: parseInt(`${i + 1}${p.id}`),
          }))
        : [],
      miscellaneous: lineItem.miscellaneous
        ? lineItem.miscellaneous.map((p, i) => ({
            ...p,
            key: parseInt(`${i + 1}${p.id}`),
          }))
        : [],
      defects: lineItem.defects || [],
      expanded: true,
      active: true,
      showServices: lineItem.services?.length > 0,
      showDefects: lineItem.defects?.length > 0,
      showPms: lineItem.pms?.length > 0,
    };
  };

  hydrateLineItems = (lineItems = []) => {
    return lineItems.map(this.hydrateLineItem);
  };

  get queryAssetId() {
    const queryParams = new URLSearchParams(this.props.history.location.search);
    return queryParams.get('assetId');
  }

  loadInspectionForm = async (workOrder) => {
    try {
      const inspectionForm = await InspectionsStore.getInspectionForm(
        ShopStore.currentShop.id,
        workOrder.inspection_form_id
      );

      this.setState({
        ...this.state,
        inspectionFormId: inspectionForm[0]?.form_id,
        inspectionFormTitle: inspectionForm[0]?.title,
      });
    } catch (error) {
      Logger.error(error);
    }
  };

  updateInspectionForm = async (workorder) => {
    if (!this.state.inspectionFormId) return;

    try {
      const payload = {
        work_order_id: workorder.id,
        work_order_number: workorder.invoice_number,
        form_id: this.state.inspectionFormId,
      };
      const shop_id = ShopStore.currentShop.id;

      InspectionsStore.updateForm(shop_id, payload);
    } catch (error) {
      Logger.error(error);
    }
  };

  load = async () => {
    // Get ID from initialWorkOrderData if in slide-out mode, otherwise from route params
    const id =
      this.props.isSlideOutMode && this.props.initialWorkOrderData
        ? this.props.initialWorkOrderData.id
        : this.props.match.params.id;

    if (id) {
      this.setState({ title: 'Edit', isEdit: true });
      // get work order by ID
      try {
        this.setState({ loadingWorkOrder: true });
        const workOrder = await WorkOrderStore.getWorkOrderById(id);

        if (!workOrder) {
          throw new Error('Work order not found');
        }
        // 2. Set the shop if needed
        if (
          workOrder &&
          workOrder.id_shop &&
          ShopStore.currentShop.id !== workOrder.id_shop
        ) {
          ShopStore.currentShop = { id: workOrder.id_shop };
          // Optionally, fetch full shop details here if needed
        }
        this.setState({ loadingInitial: true });
        await this.loadShopUsers();
        await this.loadShopVendors();
        await this.loadShopPurchaseOrders();
        await this.loadShopParts();
        this.setState({ loadingInitial: false });
        const scannedWorkOrderFile =
          workOrder.scannedWorkOrder && workOrder.scannedWorkOrder[0]?.s3_file
            ? this.transformS3Url(workOrder.scannedWorkOrder[0]?.s3_file)
            : null;
        if (scannedWorkOrderFile) {
          if (!Array.isArray(workOrder.wo_files?.wo_images)) {
            workOrder.wo_files = { ...workOrder.wo_files, wo_images: [] };
          }
          // Check if any existing files contain UFP-Workorder/staging
          const hasStagingFiles =
            (workOrder.wo_files?.wo_images || []).some((url) =>
              url.includes('UFP-Workorder/staging')
            ) ||
            (workOrder.wo_files?.wo_documents || []).some((url) =>
              url.includes('UFP-Workorder/staging')
            );

          if (!hasStagingFiles) {
            workOrder.wo_files.wo_images.unshift(scannedWorkOrderFile);
          }
        }
        // remove the cache from Issues table... to refresh the list in other components
        PitstopTableCacheStore.deleteKey(`${workOrder.id_car}-wo-issues-list`);
        // Check and update importedInformation with the first scanned work order
        // let importedInformation = null;

        this.setState(
          {
            isGeneratingWorkOrderInvoiceNumber: false,
            selectedCarId: workOrder.id_car,
            // importedInformation,
            currentWorkOrder: {
              ...workOrder,
              // those values are not being used in the first tab form
              // they are being used in the optional-information tab
              woStartDate: workOrder.started_at
                ? dayjs(workOrder.started_at)
                : null,
              woCompletedDate: workOrder.done_at
                ? dayjs(workOrder.done_at)
                : null,
              mileage: workOrder.mileage,
              engineHours: workOrder.engine_hours,
              locationCode: workOrder.location_code,
              vendor_id: workOrder.vendor_id,
              woIssueReportDate: workOrder.report_at
                ? dayjs(workOrder.report_at)
                : null,
              purchase_order_id: workOrder.purchase_order_id,
              additionalNotes: workOrder.additional_notes,
              driverReportDetails: workOrder.driver_report_details
                ? {
                    ...workOrder.driver_report_details,
                    timeOfEvent: workOrder.driver_report_details.timeOfEvent
                      ? dayjs(workOrder.driver_report_details.timeOfEvent)
                      : null,
                  }
                : {
                    driverName: '',
                    driverNumber: '',
                    location: '',
                    timeOfEvent: null,
                    weightOfVehicle: '',
                    driverNotes: '',
                    billingThrough: null,
                  },
            },
            lineItems: this.hydrateLineItems(workOrder.work_order_lines),
            initialFormValues: {
              woInvoiceNumber: workOrder.invoice_number,
              invoiceNumberCustomer: workOrder.invoice_number_customer,
              wOid: workOrder.w_ord_id,
              woCreatedBy:
                workOrder.migrate_source !== 'PitStop'
                  ? workOrder.migrate_created_by
                  : workOrder.created_by,
              woSelectVehicle: workOrder.id_car,
              woAssetType: workOrder.asset_type || 'Trailer', // default to vehicle if asset type was not provided
              woAssignedTo: workOrder.assigned_to,
              woStatus: workOrder.status,
              woRepairType: workOrder.type,
              woRepairPriority: workOrder.priority,
              woIssueReportDate: workOrder.report_at
                ? dayjs(workOrder.report_at)
                : null,
              woStartDate: workOrder.started_at
                ? dayjs(workOrder.started_at)
                : null,
              woCompletedDate: workOrder.done_at
                ? dayjs(workOrder.done_at)
                : null,
              mileage: workOrder.mileage,
              engineHours: workOrder.engine_hours,
              locationCode: workOrder.location_code,
              vendor_id: workOrder.vendor_id,
              purchase_order_id: workOrder.purchase_order_id,
              additionalNotes: workOrder.additional_notes,
              driverReportDetails: workOrder.driver_report_details
                ? {
                    ...workOrder.driver_report_details,
                    timeOfEvent: workOrder.driver_report_details.timeOfEvent
                      ? dayjs(workOrder.driver_report_details.timeOfEvent)
                      : null,
                  }
                : {
                    driverName: '',
                    driverNumber: '',
                    location: '',
                    timeOfEvent: null,
                    weightOfVehicle: '',
                    driverNotes: '',
                    billingThrough: null,
                  },
              tax: {
                type: workOrder.tax_type || 'percentage',
                value: workOrder.tax || 0,
              },
              secondTax: {
                type: workOrder.secondtax_type || 'percentage',
                value: workOrder.secondtax || 0,
              },
              misc: {
                type: workOrder.misc_type || 'percentage',
                value: workOrder.misc || 0,
              },
              warrantySavings: workOrder.warranty_savings || 0,
              woSource: workOrder.migrate_source || 'PitStop',
              rawData:
                workOrder.migrate_source !== 'PitStop'
                  ? workOrder.raw_data
                  : '',
              laborsTotal: workOrder.laborsTotal,
              partsTotal: workOrder.partsTotal,
              costsTotal: workOrder.costsTotal,
            },
            loadingWorkOrder: false,
            wo_images: workOrder.wo_files?.wo_images || [],
            wo_documents: workOrder.wo_files?.wo_documents || [],
            isDisabled: workOrder.migrate_source === 'TMT' ? true : false,
            costsTotalHasManualOverride:
              workOrder?.costs_total_has_manual_override || false,
            laboursTotalHasManualOverride:
              workOrder?.labours_total_has_manual_override || false,
            partsTotalHasManualOverride:
              workOrder?.parts_total_has_manual_override || false,
            manualOverrides: {
              manualLaboursTotal: workOrder?.manual_labours_total || 0,
              manualPartsTotal: workOrder?.manual_parts_total || 0,
              manualCostsTotal: workOrder?.manual_costs_total || 0,
            },
          },
          async () => {
            // Handle scanned data if passed from slide-out for existing work orders
            if (
              this.props.isSlideOutMode &&
              this.props.initialWorkOrderData &&
              this.props.initialWorkOrderData.extractedData
            ) {
              await this.loadWorkOrderWithExtractedInformation(
                this.props.initialWorkOrderData.extractedData,
                {
                  stringify: true,
                  fillWorkOrderNumber: false,
                  fromSlideout: true,
                }
              );

              if (this.props.initialWorkOrderData.scannedFile) {
                const scannedFile = this.props.initialWorkOrderData.scannedFile;
                const httpUrl = scannedFile.url.replace(
                  's3://work-orders-scan/',
                  'https://work-orders-scan.s3.us-east-2.amazonaws.com/'
                );

                const fileType = this.getFileTypeFromUrl(httpUrl) || 'png';
                if (
                  fileType === 'pdf' ||
                  fileType === 'tiff' ||
                  fileType === 'tif'
                ) {
                  this.setState((prevState) => ({
                    wo_documents: [httpUrl, ...(prevState.wo_documents || [])],
                  }));
                  // Render document preview for PDF/TIFF files
                  this.onClickImage(httpUrl);
                } else {
                  this.setState((prevState) => ({
                    wo_images: [httpUrl, ...(prevState.wo_images || [])],
                  }));
                  // Render image preview for image files
                  this.onClickImage(httpUrl);
                }
              }
            }

            if (
              Array.isArray(workOrder.scannedWorkOrder) &&
              workOrder.scannedWorkOrder.length > 0 &&
              workOrder.scannedWorkOrder[0]?.scanned
            ) {
              try {
                if (workOrder?.costsTotal === 0) {
                  await this.loadWorkOrderWithExtractedInformation(
                    JSON.parse(workOrder.scannedWorkOrder[0].scanned),
                    {
                      stringify: true,
                      fillWorkOrderNumber: false,
                      fromSlideout: this.props.location.state?.data
                        ? true
                        : false,
                    }
                  );
                }
                const document_type =
                  workOrder.scannedWorkOrder[0].document_type;
                if (workOrder.scannedWorkOrder[0]?.s3_file) {
                  const httpUrl = this.s3UriToHttpUrl(
                    workOrder.scannedWorkOrder[0]?.s3_file
                  );
                  this.fetchBase64FileAndRender(httpUrl, document_type);
                }
              } catch (error) {
                console.error('Failed to parse scannedWorkOrder data:', error);
              }
            }

            // load the DVIR details if there is line items with defects
            if (
              this.state.lineItems.some(
                (lineItem) =>
                  Array.isArray(lineItem?.defects) &&
                  lineItem.defects.length > 0
              )
            ) {
              this.loadDVIRsFromDefects(this.state.lineItems);
            }
          }
        );

        if (!this.props.location.state?.data?.formId) {
          this.loadInspectionForm(workOrder);
        }
      } catch (error) {
        // if abort error, ignore
        if (error?.name === 'AbortError') {
          return;
        }
        console.log(error);
        let friendlyMsg = 'Work order not found';
        this.setState({ loadingWorkOrder: false });
        return notification.error({
          message: 'Oops!',
          key: 'orderSaveError',
          description: <Container>{friendlyMsg}</Container>,
        });
      }
      const pushedData = this.props.location.state?.data;
      if (pushedData) {
        this.setState((prevState) => ({
          initialFormValues: {
            ...prevState.initialFormValues,
            ...pushedData?.formData,
            tax: pushedData?.tax || prevState.initialFormValues.tax,
            secondTax:
              pushedData?.secondTax || prevState.initialFormValues.secondTax,
            misc: pushedData?.misc || prevState.initialFormValues.misc,
            warrantySavings:
              pushedData?.warrantySavings ||
              prevState.initialFormValues.warrantySavings,
            driverReportDetails:
              pushedData?.driverReportDetails ||
              prevState.initialFormValues.driverReportDetails,
            vendor_id:
              pushedData?.vendor_id || prevState.initialFormValues.vendor_id,
          },
          lineItems: pushedData.lineItems || prevState.lineItems,
          uploadedImages: pushedData.uploadedImages || prevState.uploadedImages,
          uploadedFiles: pushedData.uploadedFiles || prevState.uploadedFiles,
          wo_images: pushedData.wo_images || prevState.wo_images,
          wo_documents: pushedData.wo_documents || prevState.wo_documents,
          currentImagePreviewSrc:
            pushedData.currentImagePreviewSrc ||
            prevState.currentImagePreviewSrc,
          previewImageUrls:
            pushedData.previewImageUrls || prevState.previewImageUrls,
          currentCarIssues:
            pushedData.currentCarIssues || prevState.currentCarIssues,
          selectedCarId: pushedData.selectedCarId || prevState.selectedCarId,
          manualOverrides: {
            ...prevState.manualOverrides,
            ...pushedData.manualOverrides,
          },
          costsTotalHasManualOverride:
            pushedData.costsTotalHasManualOverride ||
            prevState.costsTotalHasManualOverride,
          laboursTotalHasManualOverride:
            pushedData.laboursTotalHasManualOverride ||
            prevState.laboursTotalHasManualOverride,
          partsTotalHasManualOverride:
            pushedData.partsTotalHasManualOverride ||
            prevState.partsTotalHasManualOverride,
          hasUnsavedChanges: pushedData.hasUnsavedChanges || false,
        }));
        if (this.hasUnsavedChangesRef?.current !== undefined) {
          this.hasUnsavedChangesRef.current =
            pushedData.hasUnsavedChanges || false;
        }
      }
    } else {
      this.setState({ loadingInitial: true });
      await this.loadShopUsers();
      await this.loadShopVendors();
      await this.loadShopPurchaseOrders();
      await this.loadShopParts();
      this.setState({ loadingInitial: false });

      // Handle pushed data from slide-out transition
      const pushedData = this.props.location.state?.data;
      if (pushedData) {
        this.setState((prevState) => ({
          initialFormValues: {
            ...prevState.initialFormValues,
            ...pushedData?.formData,
            tax: pushedData?.tax || prevState.initialFormValues.tax,
            secondTax:
              pushedData?.secondTax || prevState.initialFormValues.secondTax,
            misc: pushedData?.misc || prevState.initialFormValues.misc,
            warrantySavings:
              pushedData?.warrantySavings ||
              prevState.initialFormValues.warrantySavings,
            driverReportDetails:
              pushedData?.driverReportDetails ||
              prevState.initialFormValues.driverReportDetails,
            vendor_id:
              pushedData?.vendor_id || prevState.initialFormValues.vendor_id,
          },
          lineItems: pushedData.lineItems || prevState.lineItems,
          uploadedImages: pushedData.uploadedImages || prevState.uploadedImages,
          uploadedFiles: pushedData.uploadedFiles || prevState.uploadedFiles,
          wo_images: pushedData.wo_images || prevState.wo_images,
          wo_documents: pushedData.wo_documents || prevState.wo_documents,
          currentImagePreviewSrc:
            pushedData.currentImagePreviewSrc ||
            prevState.currentImagePreviewSrc,
          previewImageUrls:
            pushedData.previewImageUrls || prevState.previewImageUrls,
          currentCarIssues:
            pushedData.currentCarIssues || prevState.currentCarIssues,
          selectedCarId: pushedData.selectedCarId || prevState.selectedCarId,
          manualOverrides: {
            ...prevState.manualOverrides,
            ...pushedData.manualOverrides,
          },
          costsTotalHasManualOverride:
            pushedData.costsTotalHasManualOverride ||
            prevState.costsTotalHasManualOverride,
          laboursTotalHasManualOverride:
            pushedData.laboursTotalHasManualOverride ||
            prevState.laboursTotalHasManualOverride,
          partsTotalHasManualOverride:
            pushedData.partsTotalHasManualOverride ||
            prevState.partsTotalHasManualOverride,
          hasUnsavedChanges: pushedData.hasUnsavedChanges || false,
        }));

        if (this.hasUnsavedChangesRef?.current !== undefined) {
          this.hasUnsavedChangesRef.current =
            pushedData.hasUnsavedChanges || false;
        }
      }

      let invoiceNumber = null;
      const currentInvoiceNumber = this.state.initialFormValues.woInvoiceNumber;
      try {
        // generate a unique work order invoice number
        invoiceNumber = await WorkOrderStore.generateWorkOrderInvoiceNumber();
        // this.setState({
        //   initialFormValues: {
        //     ...this.state.initialFormValues,
        //     woInvoiceNumber: invoiceNumber,
        //   },
        // });
      } catch (error) {
        console.error('Error generating work order invoice number', error);
      } finally {
        this.setState({ isGeneratingWorkOrderInvoiceNumber: false });
      }
      this.setState({ title: 'Add' });
      this.setState({ loadingWorkOrder: false });
      this.setState({ loadingWorkOrderLines: false });

      if (!invoiceNumber) {
        // something happened, generate it here on the front-end
        invoiceNumber = this.randomWorkOrderId();
      }

      // check if query contains assetId
      if (this.queryAssetId) {
        this.setState({
          selectedCarId: +this.queryAssetId,
          initialFormValues: {
            ...this.state.initialFormValues,
            woSelectVehicle: +this.queryAssetId,
            woInvoiceNumber: currentInvoiceNumber || invoiceNumber,
          },
        });
      } else {
        // update only the invoice number
        this.setState({
          initialFormValues: {
            ...this.state.initialFormValues,
            woInvoiceNumber: currentInvoiceNumber || invoiceNumber,
          },
        });
      }

      // Handle scanned data if passed from slide-out for both new and existing work orders
      if (
        this.props.isSlideOutMode &&
        this.props.initialWorkOrderData &&
        this.props.initialWorkOrderData.extractedData
      ) {
        await this.loadWorkOrderWithExtractedInformation(
          this.props.initialWorkOrderData.extractedData,
          {
            stringify: true,
            fillWorkOrderNumber: false, //invoice number is usually not a number but a string
            fromSlideout: true,
          }
        );

        if (this.props.initialWorkOrderData.scannedFile) {
          const scannedFile = this.props.initialWorkOrderData.scannedFile;
          const httpUrl = scannedFile.url.replace(
            's3://work-orders-scan/',
            'https://work-orders-scan.s3.us-east-2.amazonaws.com/'
          );

          // Get file type from URL
          const fileType = this.getFileTypeFromUrl(httpUrl) || 'png';

          // Only route based on file type if URL contains /staging/

          if (fileType === 'pdf' || fileType === 'tiff' || fileType === 'tif') {
            if (scannedFile.fileName) {
              this.setState((prevState) => ({
                wo_documents: [httpUrl, ...(prevState.wo_documents || [])],
                initialFormValues: {
                  ...prevState.initialFormValues,
                  woInvoiceNumber: scannedFile.fileName,
                },
              }));
            } else {
              this.setState((prevState) => ({
                wo_documents: [httpUrl, ...(prevState.wo_documents || [])],
              }));
            }
            // Render document preview for PDF/TIFF files
            this.onClickImage(httpUrl);
          } else {
            if (scannedFile.fileName) {
              this.setState((prevState) => ({
                wo_images: [httpUrl, ...(prevState.wo_images || [])],
                initialFormValues: {
                  ...prevState.initialFormValues,
                  woInvoiceNumber: scannedFile.fileName,
                },
              }));
            } else {
              this.setState((prevState) => ({
                wo_images: [httpUrl, ...(prevState.wo_images || [])],
              }));
            }
            // Render image preview for image files
            this.onClickImage(httpUrl);
          }
        }
      } else {
        // Don't initialize currentWorkOrder for new work orders - let it stay null
        // so the system correctly calls createWorkOrder instead of updateWorkOrder
      }
    }

    // check if state lineItems is empty. If so, add a new blank hydrated line item
    // if (!this.state.lineItems.length) {
    //   this.onAddLineItem();
    // }
  };

  transformS3Url(s3Url) {
    if (!s3Url.startsWith('s3://')) {
      throw new Error('Invalid S3 URI format');
    }

    // Extract the object key from the S3 URI
    const objectKey = s3Url.replace('s3://work-orders-scan/', '');

    // Perform the necessary transformations on the object key
    const transformedKey = objectKey
      .replace('temp_ufp_', '')
      .replace('_base64.txt', '.jpg');

    // Construct the final HTTP URL
    const bucketName = 'work-orders-scan';
    const region = 'us-east-2';
    const finalUrl = `https://${bucketName}.s3.${region}.amazonaws.com/UFP-Workorder/production/${transformedKey}`;

    return finalUrl;
  }

  // Function to get file type from URL by looking for the last dot
  getFileTypeFromUrl(url) {
    if (!url) return null;

    const lastDotIndex = url.lastIndexOf('.');
    if (lastDotIndex === -1) return null;

    return url.substring(lastDotIndex + 1).toLowerCase();
  }

  // Function to handle document preview for PDF/TIFF files - passes URL directly
  handleDocumentPreview = async (file) => {
    if (file.url) {
      // Pass the URL directly without converting to base64
      this.onClickImage(file.url);
    } else {
      // For uploaded files, just pass the URL directly
      this.onClickImage(file.url || file.thumbUrl);
    }
  };

  // Function to handle image preview - passes URL directly
  handleImagePreview = async (file) => {
    if (file.url) {
      // Pass the URL directly without converting to base64
      this.onClickImage(file.url);
    } else {
      // For uploaded files, just pass the URL directly
      this.onClickImage(file.url || file.thumbUrl);
    }
  };

  goBack = () => {
    // If in slide-out mode, close the slide-out instead of navigating
    if (this.props.isSlideOutMode) {
      EventBus.$emit('dashboardWrapper/CLOSE_SLIDE_OUT');
      return;
    }

    if (this.queryAssetId) {
      return this.props.history.push(
        `/trailer/${this.queryAssetId}/work-orders`
      );
    }
    return this.props.history.push('/work-order');
  };

  setAssetDefects = (dvirData = {}) => {
    if (!_.isEmpty(dvirData)) {
      const defectsWithDvirId = (dvirData?.payload?.vehicleDefects || []).map(
        (defect) => ({
          ...defect,
          dvirId: dvirData.dvirId,
        })
      );
      // if the defect is already on state.assetDefects, ignore it
      const defectsToAdd = defectsWithDvirId.filter(
        (defect) =>
          !this.state.assetDefects.some(
            (assetDefect) => assetDefect.id === defect.id
          )
      );
      this.setState({
        assetDefects: [...this.state.assetDefects, ...defectsToAdd],
      });
    } else {
      this.setState({
        assetDefects: [],
      });
    }
  };

  loadAssetDVIRs = async (carDetails = {}) => {
    if (!carDetails?.id) {
      return;
    }
    // check if car is Trailer or Vehicle
    const isTrailer = String(carDetails?.carType).toLowerCase() === 'trailer';
    try {
      this.setState({
        loadingAssetDefects: true,
      });
      const dvirData = await CarStore.fetchDVIRsForCars(
        carDetails.id,
        isTrailer
      );
      if (Array.isArray(dvirData) && dvirData.length) {
        const { record } = dvirData[0];
        this.setAssetDefects(record);
      }
    } catch (error) {
      console.error(error);
    } finally {
      this.setState({
        loadingAssetDefects: false,
      });
    }
  };

  loadDVIRsFromDefects = async (lineItems = []) => {
    const defects = lineItems.flatMap((lineItem) => lineItem.defects);
    const uniqueDvirIds = [...new Set(defects.map((defect) => defect.dvirId))];
    const dvirsResponse = await Promise.all(
      uniqueDvirIds.map((dvirId) => WorkOrderStore.getDvirById(dvirId))
    );
    const extractedData = dvirsResponse.map((dvir) => dvir && dvir.data);
    const dvirs = extractedData.filter((dvir) => !_.isEmpty(dvir));
    // for each DVIR, call the setAssetDefects function
    dvirs.forEach((dvir) => this.setAssetDefects(dvir));
  };

  onCarSelect = async (selectedCarId) => {
    if (!selectedCarId) {
      return;
    }

    this.setState({ selectedCarId: +selectedCarId });
    // load car issues
    try {
      const filter = {};
      const id = this.props.match.params.id;
      if (!id) {
        filter.status = 'new'; // only get new issues if adding a new work order
      }
      const carIssues = await IssueStore.getCarIssuesList(
        {
          offset: 0,
          limit: 200,
          filter,
          sort: null,
          includeHistory: false,
        },
        selectedCarId
      );
      this.setState({
        currentCarIssues: carIssues,
      });
    } catch (error) {
      notification.error({
        message: 'Error',
        key: 'carIssues',
        description: <Container>{error.message}</Container>,
      });
    }
  };

  onChangeAssignedTo = (value) => {
    if (!this.state.lineItems || this.state.lineItems.length === 0) {
      return this.onAddLineItem([
        {
          key: new Date().getTime(),
          id_labour: value,
          hours: 0,
          cost: 0,
        },
      ]);
    }

    // change the first line item assigned to
    const newLineItems = JSON.parse(JSON.stringify(this.state.lineItems));
    // get the first line item and check if there's a labor already
    if (
      Array.isArray(newLineItems[0]?.labours) &&
      newLineItems[0].labours.length === 0
    ) {
      newLineItems[0].labours.push({
        key: new Date().getTime(),
        id_labour: value,
        hours: 0,
        cost: 0,
      });
    } else {
      newLineItems[0].labours[0].id_labour = value;
    }

    this.setState({
      lineItems: newLineItems,
    });
  };

  onLocationSelect = (value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        locationCode: value,
      },
    });
  };

  onChangePurchaseOrder = (value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        purchaseOrder: value,
      },
    });
  };

  onChangeTaxType = (type) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        tax: {
          ...this.state.initialFormValues.tax,
          type,
        },
      },
    });
  };

  onChangeTaxValue = (value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        tax: {
          ...this.state.initialFormValues.tax,
          value,
        },
      },
    });
  };

  onChangeSecondTaxType = (type) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        secondTax: {
          ...this.state.initialFormValues.secondTax,
          type,
        },
      },
    });
  };

  onChangeSecondTaxValue = (value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        secondTax: {
          ...this.state.initialFormValues.secondTax,
          value,
        },
      },
    });
  };

  onChangeMiscType = (type) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        misc: {
          ...this.state.initialFormValues.misc,
          type,
        },
      },
    });
  };

  onChangeMiscValue = (value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        misc: {
          ...this.state.initialFormValues.misc,
          value,
        },
      },
    });
  };

  updateLineItemHeader = (e, directValue = '', lineItem) => {
    let value;
    if (e) {
      value = e.target.value;
    } else {
      value = directValue;
    }
    if (!value) {
      lineItem.header = `New Work Order Line ${lineItem.key}`;
    } else {
      lineItem.header = value;
    }
  };

  onAddLineItem = (withLabors = []) => {
    const lineItemsArray = this.state.lineItems || [];
    const key = lineItemsArray.length + 1;
    const newLineItems = JSON.parse(JSON.stringify(lineItemsArray));
    newLineItems.push({
      header: `New Work Order Line ${key}`,
      key: key,
      title: `New Work Order Line ${key}`,
      description: '',
      laborTotal: 0,
      partsTotal: 0,
      labours: withLabors,
      parts: [],
      services: [],
      miscellaneous: [],
      defects: [],
      expanded: true,
    });
    this.setState({
      lineItems: newLineItems,
    });
  };

  onChangeLineItemField = (lineKey, newVal, fieldName) => {
    const indexOfLineItem = this.state.lineItems.findIndex(
      (item) => item.key === lineKey
    );
    const lineItem = this.state.lineItems.find((item) => item.key === lineKey);
    const newLineItems = JSON.parse(JSON.stringify(this.state.lineItems));
    lineItem[fieldName] = newVal;
    newLineItems.splice(indexOfLineItem, 1, {
      ...lineItem,
    });
    this.setState({
      lineItems: newLineItems,
    });
  };

  onRemoveLineItem = (itemKey) => {
    const indexOfLineItem = this.state.lineItems.findIndex(
      (item) => item.key === itemKey
    );
    const newLineItems = JSON.parse(JSON.stringify(this.state.lineItems));
    newLineItems.splice(indexOfLineItem, 1);
    this.setState({
      lineItems: newLineItems,
    });
  };

  createWorkOrder = async (payload) => {
    // eslint-disable-next-line no-useless-catch
    try {
      const response = await WorkOrderStore.createWorkOrderOnCustomEndpoint({
        workOrder: {
          id_car: +payload.woSelectVehicle,
          asset_type: payload.woAssetType,
          id_shop: ShopStore.currentShop.id,
          status: payload.woStatus,
          type: payload.woRepairType,
          priority: payload.woRepairPriority,
          invoice_number: payload.woInvoiceNumber,
          invoice_number_customer: payload.invoiceNumberCustomer,
          assigned_to: +payload.woAssignedTo,
          created_by: +payload.woCreatedBy,
          tax: this.state.initialFormValues?.tax?.value || null,
          tax_type: this.state.initialFormValues?.tax?.type || null,
          secondtax: this.state.initialFormValues?.secondTax?.value || null,
          secondtax_type: this.state.initialFormValues?.secondTax?.type || null,
          misc: this.state.initialFormValues?.misc?.value || null,
          misc_type: this.state.initialFormValues?.misc?.type || null,
          warranty_savings:
            this.state.initialFormValues?.warrantySavings || null,
          migrate_source: 'PitStop',
          started_at: payload.woStartDate
            ? moment(payload.woStartDate).toDate()
            : null,
          done_at: payload.woCompletedDate
            ? moment(payload.woCompletedDate).toDate()
            : null,
          mileage: payload.mileage,
          engine_hours: payload.engineHours,
          location_code: payload.locationCode,
          vendor_id: payload.vendor_id,
          report_at: payload.woIssueReportDate
            ? moment(payload.woIssueReportDate).toDate()
            : null,
          additional_notes: payload.additionalNotes,
          driver_report_details: payload.driverReportDetails,
          purchase_order_id: payload.purchase_order_id,
          wo_files: payload.wo_files,
          costsTotal: this.costSummaryRef?.total || 0,
          partsTotal: this.costSummaryRef?.calculatedPartsTotal || 0,
          laborsTotal: this.costSummaryRef?.calculatedLaborsTotal || 0,
          miscTotal: this.costSummaryRef?.calculatedMiscellaneousTotal || 0,
          costs_total_has_manual_override:
            payload.costs_total_has_manual_override,
          manual_costs_total: payload.manual_costs_total,
          labours_total_has_manual_override:
            payload.labours_total_has_manual_override,
          manual_labours_total: payload.manual_labours_total,
          parts_total_has_manual_override:
            payload.parts_total_has_manual_override,
          manual_parts_total: payload.manual_parts_total,
          inspection_form_id: this.state.inspectionFormId || null,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  updateWorkOrder = async (payload) => {
    const { woStatus } = this.workOrderFormRef.getFieldsValue();
    payload.woStatus = woStatus;
    const invoiceNumber = payload.wOid
      ? this.state.currentWorkOrder?.invoice_number
      : payload.woInvoiceNumber;
    const response = await WorkOrderStore.updateWorkOrderOnCustomEndpoint({
      id: this.state.currentWorkOrder?.id,
      id_car: +payload.woSelectVehicle,
      asset_type: payload.woAssetType,
      status: payload.woStatus,
      type: payload.woRepairType,
      priority: payload.woRepairPriority,
      invoice_number: invoiceNumber,
      invoice_number_customer: payload.invoiceNumberCustomer,
      w_ord_id: payload.woInvoiceNumber,
      assigned_to: +payload.woAssignedTo,
      created_by: +payload.woCreatedBy,
      tax: this.state.initialFormValues?.tax?.value || null,
      tax_type: this.state.initialFormValues?.tax?.type || null,
      secondtax: this.state.initialFormValues?.secondTax?.value || null,
      secondtax_type: this.state.initialFormValues?.secondTax?.type || null,
      misc: this.state.initialFormValues?.misc?.value || null,
      misc_type: this.state.initialFormValues?.misc?.type || null,
      warranty_savings: this.state.initialFormValues?.warrantySavings || null,
      started_at: payload.woStartDate
        ? moment(payload.woStartDate).toDate()
        : null,
      done_at: payload.woCompletedDate
        ? moment(payload.woCompletedDate).toDate()
        : null,
      mileage: payload.mileage,
      engine_hours: payload.engineHours,
      vendor_id: payload.vendor_id,
      report_at: payload.woIssueReportDate
        ? moment(payload.woIssueReportDate).toDate()
        : null,
      additional_notes: payload.additionalNotes,
      driver_report_details: payload.driverReportDetails,
      purchase_order_id: payload.purchase_order_id,
      wo_files: payload.wo_files,
      costsTotal: this.costSummaryRef?.total || 0,
      partsTotal: this.costSummaryRef?.calculatedPartsTotal || 0,
      laborsTotal: this.costSummaryRef?.calculatedLaborsTotal || 0,
      // miscTotal: this.costSummaryRef?.calculatedMiscellaneousTotal || 0,
      costs_total_has_manual_override: payload.costs_total_has_manual_override,
      manual_costs_total: payload.manual_costs_total,
      labours_total_has_manual_override:
        payload.labours_total_has_manual_override,
      manual_labours_total: payload.manual_labours_total,
      parts_total_has_manual_override: payload.parts_total_has_manual_override,
      manual_parts_total: payload.manual_parts_total,
      location_code: payload.locationCode,
    });

    // Check if we need to Resolve a DVIR
    if (
      payload.woStatus === 'resolved' &&
      payload.woRepairType === 'driver_identified' &&
      Array.isArray(this.state.currentWorkOrder?.work_order_dvirs) &&
      this.state.currentWorkOrder?.work_order_dvirs.length > 0
    ) {
      if (!CurrentUserStore.user.email) {
        throw new Error('An user is required to resolve the DVIR');
      }

      const samsaraUsers = await CarStore.fetchSamsaraUsers(
        ShopStore.currentShop.id
      );
      // Select the current user email if it's in the list
      const currentUserEmail = CurrentUserStore.user.email;
      const samsaraUserResolving = samsaraUsers.find(
        (user) =>
          String(user.email).toLowerCase() ===
          String(currentUserEmail).toLowerCase()
      );

      let defects = [];
      let dvirId = this.state.currentWorkOrder?.work_order_dvirs[0].dvir_id;
      this.state.currentWorkOrder?.work_order_dvirs.forEach((dvirDefects) => {
        defects.push({ id: dvirDefects.defect_id.toString() });
      });

      if (dvirId && Array.isArray(defects) && defects.length > 0) {
        const defectsResolved = await CarStore.resolveDVIRDefects({
          shopId: ShopStore.currentShop.id,
          dvirId: dvirId,
          defects: defects,
          mechanicNotes: 'Resolved by Pitstop',
          resolvedBySamsaraUserId: samsaraUserResolving?.id || currentUserEmail,
        });
        AppStore.addNotification(
          `${defectsResolved && Array.isArray(defectsResolved) ? defectsResolved.length : 0} defects resolved successfully`
        );
      }
    }
    return response;
  };

  onClickAddPurchaseOrder = () => {
    this.setState({ addPOModalVisible: true });
  };

  onAddPurchaseOrder = async (newPurchaseOrder) => {
    this.setState({
      addPOModalVisible: false,
    });
    if (newPurchaseOrder) {
      try {
        const newPO =
          await PurchaseOrderStore.savePurchaseOrder(newPurchaseOrder);
        AppStore.addSuccess('Purchase Order added successfully');
        await this.loadShopPurchaseOrders();
        // pre-select the purchase order
        this.setState({
          initialFormValues: {
            ...this.state.initialFormValues,
            purchaseOrder: newPO.id,
            purchase_order_id: newPO.id,
          },
        });
      } catch (error) {
        AppStore.addError('Error adding Purchase Order');
      }
    }
  };

  saveWorkOrder = async () => {
    const saveWorkOrderAfterValidation = async (payload) => {
      // Patch edited fields from latest form values if available
      const form = this.workOrderFormRef.form || this.workOrderFormRef;
      const latestFormValues = form.getFieldsValue();
      if (latestFormValues.locationCode) {
        payload.locationCode = latestFormValues.locationCode;
      }
      if (latestFormValues.additionalNotes !== undefined) {
        payload.additionalNotes = latestFormValues.additionalNotes;
      }
      if (latestFormValues.invoiceNumberCustomer !== undefined) {
        payload.invoiceNumberCustomer = latestFormValues.invoiceNumberCustomer;
      }
      // override the vendor_id if it's changed. Needs to do the same for all the fields?
      // TODO: better check this
      if (this.state.initialFormValues.vendor_id !== undefined) {
        payload.vendor_id = this.state.initialFormValues.vendor_id;
      }
      let workOrder;
      let woFileAndImages = {};
      if (
        this.state.activeTab === 'optional-information' ||
        !this.state.isMobileScreenSize // we know that if not mobile, the optional info is rendered
      ) {
        const woImages = await this.uploadWorkOrderImages();
        const woFiles = await this.uploadWorkOrderFiles();

        const filteredWoImages = woImages.filter(
          (url) =>
            !url.startsWith(
              'https://work-orders-scan.s3.us-east-2.amazonaws.com/UFP-Workorder/production'
            )
        );
        woFileAndImages.wo_files = {
          wo_images: filteredWoImages,
          wo_documents: woFiles,
        };
      } else {
        // not in the optional-information tab, so if updating a work order, we need to keep the images and files
        if (this.state.currentWorkOrder) {
          woFileAndImages.wo_files = this.state.currentWorkOrder?.wo_files;
        }
      }
      // if there are importedCostsTotal, add to the payload
      const importedCostsTotal = {};
      if (this.state.importedInformation) {
        const objImportedInfo = JSON.parse(this.state.importedInformation);
        if (
          objImportedInfo &&
          objImportedInfo['Labour Cost'] &&
          objImportedInfo['Labour Cost'] !== 'NOT FOUND'
        ) {
          importedCostsTotal.laborsTotal = parseFloat(
            objImportedInfo['Labour Cost']
          );
        }
        if (
          objImportedInfo &&
          objImportedInfo['Parts Cost'] &&
          objImportedInfo['Parts Cost'] !== 'NOT FOUND'
        ) {
          importedCostsTotal.partsTotal = parseFloat(
            objImportedInfo['Parts Cost']
          );
        }
        if (
          objImportedInfo &&
          objImportedInfo['Total Cost'] &&
          objImportedInfo['Total Cost'] !== 'NOT FOUND'
        ) {
          importedCostsTotal.costsTotal = parseFloat(
            objImportedInfo['Total Cost']
          );
        }
      }
      // always check if the work order has manual overrides
      const manualOverrides = {};

      if (this.state.costsTotalHasManualOverride) {
        manualOverrides.costs_total_has_manual_override = true;
        manualOverrides.manual_costs_total =
          this.state.manualOverrides.manualCostsTotal;
      } else {
        manualOverrides.costs_total_has_manual_override = false;
        manualOverrides.manual_costs_total = 0;
      }
      if (this.state.laboursTotalHasManualOverride) {
        manualOverrides.labours_total_has_manual_override = true;
        manualOverrides.manual_labours_total =
          this.state.manualOverrides.manualLaboursTotal;
      } else {
        manualOverrides.labours_total_has_manual_override = false;
        manualOverrides.manual_labours_total = 0;
      }
      if (this.state.partsTotalHasManualOverride) {
        manualOverrides.parts_total_has_manual_override = true;
        manualOverrides.manual_parts_total =
          this.state.manualOverrides.manualPartsTotal;
      } else {
        manualOverrides.parts_total_has_manual_override = false;
        manualOverrides.manual_parts_total = 0;
      }

      // If it's an existing work order, update it. Otherwise, create a new one
      if (this.state.currentWorkOrder) {
        // we hydrate the payload with some extra information that is not in the form
        const modifiedPayload = {
          ...payload,
          ...woFileAndImages,
          ...importedCostsTotal,
          ...manualOverrides,
        };
        workOrder = await this.updateWorkOrder(modifiedPayload);
      } else {
        // create a work order if there's no current work order
        const modifiedPayload = {
          ...payload,
          ...woFileAndImages,
          ...importedCostsTotal,
          ...manualOverrides,
        };
        workOrder = await this.createWorkOrder(modifiedPayload);

        // Emit work order created event for global listeners
        EventBus.$emit('workOrders:created', workOrder);

        // If this is a new work order created from SFTP scanned data, update the file event
        if (
          workOrder &&
          this.props.isSlideOutMode &&
          this.props.initialWorkOrderData &&
          this.props.initialWorkOrderData.scannedFile
        ) {
          try {
            const scannedFile = this.props.initialWorkOrderData.scannedFile;
            const shopId = ShopStore.currentShop.id;

            // Update the SFTP file event with the newly created work order ID
            const workOrderId =
              workOrder.w_ord_id || workOrder.invoice_number || workOrder.id;

            await WorkOrderStore.updateFileEvents({
              eventId: scannedFile.eventId,
              workOrderId: workOrderId,
              updatedBy: CurrentUserStore.user.email,
              shopId: shopId,
            });

            // Show success message
            notification.success({
              message: 'Work Order Created Successfully',
              description: `Work order #${workOrder.invoice_number || workOrder.id} has been created and linked to the scanned file.`,
            });

            // Emit an event to refresh the SFTP table
            EventBus.$emit('sftp-work-orders/refresh');
          } catch (error) {
            console.error('Error updating SFTP file event:', error);
            notification.warning({
              message: 'Work Order Created',
              description:
                'Work order was created successfully, but there was an issue linking it to the scanned file.',
            });
          }
        }
      }

      // clear uploaded files and images
      this.setState({
        uploadedFiles: [],
        uploadedImages: [],
        wo_images: [],
        wo_documents: [],
      });

      return workOrder;
    };

    const getPayloadFromCurrentWorkOrder = () => {
      return {
        woInvoiceNumber: this.state.currentWorkOrder?.invoice_number,
        invoiceNumberCustomer:
          this.state.currentWorkOrder?.invoice_number_customer,
        woCreatedBy: this.state.currentWorkOrder?.created_by,
        woSelectVehicle: this.state.currentWorkOrder?.id_car,
        woAssetType: this.state.currentWorkOrder?.asset_type,
        woAssignedTo: this.state.currentWorkOrder?.assigned_to,
        woStatus: this.state.currentWorkOrder?.status,
        woRepairType: this.state.currentWorkOrder?.type,
        woRepairPriority: this.state.currentWorkOrder?.priority,
        // woIssueReportDate: this.state.currentWorkOrder.created_at,
        woStartDate:
          this.state.currentWorkOrder?.woStartDate ||
          this.state.currentWorkOrder?.started_at,
        woCompletedDate:
          this.state.currentWorkOrder?.woCompletedDate ||
          this.state.currentWorkOrder?.done_at,
        mileage:
          this.state.currentWorkOrder?.mileage ||
          this.state.currentWorkOrder?.mileage,
        engineHours:
          this.state.currentWorkOrder?.engineHours ||
          this.state.currentWorkOrder?.engine_hours,
        locationCode: this.state.currentWorkOrder?.location_code,
        tax: {
          type: this.state.currentWorkOrder?.tax_type,
          value: this.state.currentWorkOrder?.tax,
        },
        secondTax: {
          type: this.state.currentWorkOrder?.second_tax_type,
          value: this.state.currentWorkOrder?.second_tax,
        },
        misc: {
          type: this.state.currentWorkOrder?.misc_type,
          value: this.state.currentWorkOrder?.misc,
        },
        warrantySavings: this.state.currentWorkOrder?.warranty_savings,
        vendor_id: this.state.currentWorkOrder?.vendor_id,
        purchase_order_id: this.state.currentWorkOrder?.purchase_order_id,
        additionalNotes: this.state.currentWorkOrder?.additional_notes,
        driverReportDetails: this.state.currentWorkOrder?.driver_report_details,
        woIssueReportDate:
          this.state.currentWorkOrder?.woIssueReportDate ||
          this.state.currentWorkOrder?.report_at,
        woSource: this.state.currentWorkOrder?.migrate_source,
        rawData: this.state.currentWorkOrder?.raw_data,
        laborsTotal: this.state.currentWorkOrder?.laborsTotal,
        partsTotal: this.state.currentWorkOrder?.partsTotal,
        // get the costsTotal from the summary component. It's already applying all the necessary logic
        costsTotal:
          this.costSummaryRef?.total ||
          0 ||
          this.state.currentWorkOrder?.costsTotal,
        wOid: this.state.currentWorkOrder?.w_ord_id,
      };
    };

    try {
      if (
        this.state.workOrderFormData &&
        Object.keys(this.state.workOrderFormData).length > 0
      ) {
        let payload = { ...this.state.workOrderFormData };
        payload.wOid =
          this.state.initialFormValues.wOid ||
          this.state.currentWorkOrder?.w_ord_id;

        if (
          this.state.activeTab === 'optional-information' ||
          !this.state.isMobileScreenSize
        ) {
          payload.purchase_order_id =
            this.state.initialFormValues.purchase_order_id;
          payload.woIssueReportDate =
            this.state.initialFormValues.woIssueReportDate;
          payload.woStartDate = this.state.initialFormValues.woStartDate;
          payload.woCompletedDate =
            this.state.initialFormValues.woCompletedDate;
          payload.mileage = this.state.initialFormValues.mileage;
          payload.engineHours = this.state.initialFormValues.engineHours;
          payload.locationCode = this.state.initialFormValues.locationCode;
          payload.vendor_id = this.state.initialFormValues.vendor_id;
          payload.additionalNotes =
            this.state.initialFormValues.additionalNotes;
          payload.driverReportDetails =
            this.state.initialFormValues.driverReportDetails;

          if (payload.woStartDate) {
            payload.woStartDate = payload.woStartDate.toDate();
          }
          if (payload.woCompletedDate) {
            payload.woCompletedDate = payload.woCompletedDate.toDate();
          }
          if (payload.woIssueReportDate) {
            payload.woIssueReportDate = payload.woIssueReportDate.toDate();
          }
        }

        return await saveWorkOrderAfterValidation(payload);
      } else if (this.state.currentWorkOrder) {
        const payload = getPayloadFromCurrentWorkOrder();
        return await saveWorkOrderAfterValidation(payload);
      } else {
        return new Promise((resolve, reject) => {
          const form = this.workOrderFormRef.form || this.workOrderFormRef;

          form
            .validateFields()
            .then(async (payload) => {
              try {
                if (isEmpty(payload) && this.state.currentWorkOrder) {
                  payload = getPayloadFromCurrentWorkOrder();
                } else {
                  // check if the current tab is optional-information
                  payload.wOid =
                    this.state.initialFormValues.wOid ||
                    this.state.currentWorkOrder?.w_ord_id;
                  if (
                    this.state.activeTab === 'optional-information' ||
                    !this.state.isMobileScreenSize
                  ) {
                    // add to payload the optional information
                    payload.purchase_order_id =
                      this.state.initialFormValues.purchase_order_id;
                    payload.woIssueReportDate =
                      this.state.initialFormValues.woIssueReportDate;
                    payload.woStartDate =
                      this.state.initialFormValues.woStartDate;
                    payload.woCompletedDate =
                      this.state.initialFormValues.woCompletedDate;
                    payload.mileage = this.state.initialFormValues.mileage;
                    payload.engineHours =
                      this.state.initialFormValues.engineHours;
                    payload.locationCode =
                      this.state.initialFormValues.locationCode;
                    payload.vendor_id = this.state.initialFormValues.vendor_id;
                    payload.additionalNotes =
                      this.state.initialFormValues.additionalNotes;
                    payload.driverReportDetails =
                      this.state.initialFormValues.driverReportDetails;

                    // convert from moment to date
                    if (payload.woStartDate) {
                      payload.woStartDate = payload.woStartDate.toDate();
                    }
                    if (payload.woCompletedDate) {
                      payload.woCompletedDate =
                        payload.woCompletedDate.toDate();
                    }
                    if (payload.woIssueReportDate) {
                      payload.woIssueReportDate =
                        payload.woIssueReportDate.toDate();
                    }
                  }
                }
                const workOrder = await saveWorkOrderAfterValidation(payload);
                resolve(workOrder);
              } catch (error) {
                reject(error);
              }
            })
            .catch((error) => {
              this.setState({ savingWorkOrder: false });
              AppStore.removeLoading('Saving Work Order...');

              let friendlyMsg = '';
              if (error?.errorFields && error.errorFields.length > 0) {
                friendlyMsg = error.errorFields[0]?.errors[0] || friendlyMsg;
              } else {
                friendlyMsg = 'Error on saving Work Order';
              }

              notification.error({
                message: 'Oops!',
                key: 'orderSaveError',
                description: <Container>{friendlyMsg}</Container>,
              });

              reject(error);
            });
        });
      }
    } catch (error) {
      this.setState({ savingWorkOrder: false });
      AppStore.removeLoading('Saving Work Order...');

      notification.error({
        message: 'Oops!',
        key: 'orderSaveError',
        description: (
          <Container>{error.message || 'Failed to save work order'}</Container>
        ),
      });

      throw error;
    }
  };

  formatWorkOrderLines = async (lines) => {
    return await Promise.all(
      lines.map(async (workOrderLine) => {
        const updatingData = {};
        if (workOrderLine.id) {
          updatingData.id = workOrderLine.id;
        }
        // remove empty parts (without a part selected)
        const parts = workOrderLine.parts.filter((p) => p.id_vehicle_part);
        // for now, we're going to force the selected labour if it's empty... To allow
        // the user to add a line item without any labor
        const labours = workOrderLine.labours.map((l) => {
          // check if id_labour is part of shop users
          // if not, set it to null

          l = Object.fromEntries(
            Object.entries(l).filter(
              ([_, value]) =>
                value !== null && value !== undefined && value !== ''
            )
          );

          const shopUsers = this.shopUsers;
          const isSelectedLaborAShopUser = shopUsers.find(
            (user) =>
              user.id ===
              parseInt(l.labourTechnician?.id_technician || l.id_labour, 10)
          );
          if (isSelectedLaborAShopUser) {
            return l;
          }
          return {
            ...l,
            id_labour: this.state.currentWorkOrder?.assigned_to,
          };
        });

        // if vmrs_sys_code_id is provided, find the vmrs record
        if (workOrderLine.vmrs_sys_code_id) {
          const response = await WorkOrderStore.getVmrsCodeById(
            workOrderLine.vmrs_sys_code_id
          );
          const vmrsSysCode = response?.data;
          if (vmrsSysCode) {
            updatingData.vmrs_sys_text = vmrsSysCode.text;
            updatingData.vmrs_sys_code = vmrsSysCode.sys_text;
          }
        }

        // if vmrs_asm_code_id is provided, find the vmrs record
        if (workOrderLine.vmrs_asm_code_id) {
          const response = await WorkOrderStore.getVmrsCodeById(
            workOrderLine.vmrs_asm_code_id
          );
          const vmrsAsmCode = response?.data;
          if (vmrsAsmCode) {
            updatingData.vmrs_asm_text = vmrsAsmCode.text;
            updatingData.vmrs_asm_code = vmrsAsmCode.asm_text;
          }
        }

        return {
          ...updatingData,
          title:
            workOrderLine.title || `New Work Order Line ${workOrderLine.key}`,
          description: workOrderLine.description,
          vmrs_sys_code_id: workOrderLine.vmrs_sys_code_id,
          vmrs_asm_code_id: workOrderLine.vmrs_asm_code_id,
          // always round the labour_cost to 2 decimal places
          labour_cost: parseFloat(
            parseFloat(workOrderLine.laborTotal).toFixed(2)
          ),
          parts_cost: parseFloat(
            parseFloat(workOrderLine.partsTotal).toFixed(2)
          ),
          misc_cost: parseFloat(parseFloat(workOrderLine.miscTotal).toFixed(2)),
          parts: parts.map((p) => ({
            ...p,
            is_external_part: p.is_external_part || false,
            id_vehicle_part: p.id_vehicle_part,
            amount: p.amount ? parseFloat(p.amount) : 0,
            originalAmount: p.originalAmount ? parseFloat(p.originalAmount) : 0,
            markup: p.markup ? parseFloat(p.markup) : 0,
            cost: p.cost ? parseFloat(parseFloat(p.cost).toFixed(2)) : 0,
            volumeunit: p.volumeunit || '',
          })),
          labours: labours.map((l) => ({
            ...l,
            id_labour: l.id_labour,
            hours: l.hours || 0,
            cost: parseFloat(l.cost || 0),
          })),
          services: workOrderLine.services.map((i) => ({
            ...i,
            issue: i.issue,
            id_vehicle_service: +i.id_vehicle_service,
          })),
          miscellaneous: workOrderLine.miscellaneous,
          defects: workOrderLine.defects || [],
          ...(workOrderLine.diagnosis &&
            Object.keys(workOrderLine.diagnosis).length > 0 && {
              diagnosis: workOrderLine.diagnosis,
            }),
          ...(workOrderLine.vmrs_full_code &&
            workOrderLine.vmrs_full_description && {
              vmrs_full_code: workOrderLine.vmrs_full_code,
              vmrs_full_description: workOrderLine.vmrs_full_description,
            }),
          vmrs_full_code_ai: false,
          ...(workOrderLine.reason && {
            reason: parseInt(workOrderLine.reason),
          }),
          ...(workOrderLine.pms &&
            workOrderLine.pms?.length > 0 && { pms: workOrderLine.pms }),
          ...(workOrderLine.vmrs_asm_code && {
            vmrs_asm_code: workOrderLine.vmrs_asm_code,
            vmrs_asm_text: workOrderLine.vmrs_asm_text,
          }),
          ...(workOrderLine.vmrs_sys_code && {
            vmrs_sys_code: workOrderLine.vmrs_sys_code,
            vmrs_sys_text: workOrderLine.vmrs_sys_text,
          }),
          ...(workOrderLine.vmrs_comp_code && {
            vmrs_comp_code: workOrderLine.vmrs_comp_code,
            vmrs_comp_text: workOrderLine.vmrs_comp_text,
          }),
          ...(workOrderLine.inspection_issues && {
            inspection_issues: workOrderLine.inspection_issues,
          }),
        };
      })
    );
  };

  saveWorkOrderLines = async (workOrder) => {
    try {
      this.setState({ savingWorkOrderLines: true });

      const formattedWorkOrderLines = await this.formatWorkOrderLines(
        this.state.lineItems
      );

      const payloadToSave = {
        workOrderLines: formattedWorkOrderLines,
      };

      const response = await WorkOrderStore.saveWorkOrderLinesOnCustomEndpoint(
        +workOrder.id,
        payloadToSave
      );

      // update parts unit cost on shop
      // const lineItem = _.filter(this.state.lineItems, (lineItem) => {
      //   return lineItem.parts && lineItem.parts.length;
      // });
      // for (const lineItemWithParts of lineItem) {
      //   for (const part of lineItemWithParts.parts) {
      //     if (!part.id_vehicle_part) {
      //       continue;
      //     }
      //     await WorkOrderStore.updatePartOnCustomEndpoint({
      //       id: part.id_vehicle_part,
      //       cost: part.cost,
      //       markup: part.markup,
      //     });
      //   }
      // }

      // // update parts stock
      // const workOrderLines = _.filter(formattedWorkOrderLines, (lineItem) => {
      //   return lineItem.parts && lineItem.parts.length;
      // });
      // for (const lineItemWithParts of workOrderLines) {
      //   for (const part of lineItemWithParts.parts) {
      //     if (!part.id_vehicle_part) {
      //       continue;
      //     }
      //     await WorkOrderStore.updatePartStock(part);
      //   }
      // }

      return response;
    } finally {
      this.setState({ savingWorkOrderLines: false });
    }
  };

  validateCosts = () => {
    const data = JSON.parse(this.state.importedInformation);
    const scannedPartsCost = data['Parts Cost'];
    const scannedLabourCost = data['Labour Cost'];

    // Parts
    const partsCostFromLineItems = _.sumBy(this.state.lineItems, 'partsTotal');
    if (partsCostFromLineItems !== scannedPartsCost) {
      const partsCostFromLineItemsToShow = _.isNumber(partsCostFromLineItems)
        ? partsCostFromLineItems.toFixed(2)
        : partsCostFromLineItems;
      const scannedPartsCostToShow = _.isNumber(scannedPartsCost)
        ? scannedPartsCost.toFixed(2)
        : scannedPartsCost;
      this.showWarningModal(
        'Parts Cost',
        partsCostFromLineItemsToShow,
        scannedPartsCostToShow
      );
      this.setState({ isPartsCostDiscrepancy: true, isPartsWarning: true });
    } else {
      this.setState({ isPartsCostDiscrepancy: false, isPartsWarning: false });
    }

    // Labours
    const laborCostFromLineItems = _.sumBy(this.state.lineItems, 'laborTotal');
    if (laborCostFromLineItems !== scannedLabourCost) {
      const laborCostFromLineItemsToShow = _.isNumber(laborCostFromLineItems)
        ? laborCostFromLineItems.toFixed(2)
        : laborCostFromLineItems;
      const scannedLabourCostToShow = _.isNumber(scannedLabourCost)
        ? scannedLabourCost.toFixed(2)
        : scannedLabourCost;
      this.showWarningModal(
        'Labor Cost',
        laborCostFromLineItemsToShow,
        scannedLabourCostToShow
      );
      this.setState({ isLaborCostDiscrepancy: true, isLabourWarning: true });
    } else {
      this.setState({ isLaborCostDiscrepancy: false, isLabourWarning: false });
    }
  };

  showWarningModal = (type, summedCost, scannedCost) => {
    Modal.warning({
      title: `${type} Discrepancy`,
      content: (
        <div>
          {type === 'Parts Cost' ? (
            <>
              The sum of <strong>Parts</strong> costs in line items is different
              from the
              <strong> Parts Cost </strong> value our AI has found. Please
              verify the accuracy of your line items as our AI has found the{' '}
              <strong>Parts Total for scanned items</strong> to be{' '}
              <strong> ${scannedCost} </strong>
              but the <strong>sum</strong> of parts costs from your{' '}
              <strong>service tasks</strong> is <strong> ${summedCost} </strong>
              .
            </>
          ) : (
            <>
              The sum of <strong>Labor</strong> costs in line items is different
              from the
              <strong> Labor Cost </strong> value our AI has found. Please
              verify the accuracy of your line items as our AI has found the{' '}
              <strong>Labor Total for scanned items</strong> to be{' '}
              <strong>${scannedCost} </strong>
              but the <strong>sum</strong> of labor costs from your{' '}
              <strong>service tasks</strong> is <strong>${summedCost} </strong>.
            </>
          )}
        </div>
      ),
      onOk: () => this.onModalClose(type),
    });
  };

  onModalClose = (type) => {
    if (type === 'Parts Cost') {
      this.setState({ isPartsWarning: false });
    } else if (type === 'Labor Cost') {
      this.setState({ isLabourWarning: false });
    }
  };

  onClickSave = async (nextBehavior = 'next-section', pathname = null) => {
    try {
      // Validate the form before saving
      if (this.workOrderFormRef) {
        const form = this.workOrderFormRef.form || this.workOrderFormRef;
        try {
          await form.validateFields();
        } catch (error) {
          if (
            Array.isArray(error.errorFields) &&
            error.errorFields.length > 0
          ) {
            notification.error({
              message: 'Oops!',
              description: error.errorFields[0].errors[0],
            });
            return;
          }
        }
      }

      // save the work order first...
      const isNewWorkOrder = !this.state.currentWorkOrder;
      this.setState({ savingWorkOrder: true });
      AppStore.addLoading('Saving Work Order...');
      const newWorkOrder = await this.saveWorkOrder();

      this.updateInspectionForm(newWorkOrder);

      AppStore.removeLoading('Saving Work Order...');
      if (!newWorkOrder) {
        return;
      }

      // save the newWorkOrder on current state
      this.setState({
        currentWorkOrder: {
          ...newWorkOrder,
          // those values are not being used in the first tab form
          // they are being used in the optional-information tab
          woStartDate: newWorkOrder.started_at
            ? moment(newWorkOrder.started_at)
            : null,
          woCompletedDate: newWorkOrder.done_at
            ? moment(newWorkOrder.done_at)
            : null,
          woIssueReportDate: newWorkOrder.report_at
            ? moment(newWorkOrder.report_at)
            : null,
          mileage: newWorkOrder.mileage,
          engineHours: newWorkOrder.engine_hours,
          locationCode: newWorkOrder.location_code,
          vendor_id: newWorkOrder.vendor_id,
          purchase_order_id: newWorkOrder.purchase_order_id,
          additionalNotes: newWorkOrder.additional_notes,
          driverReportDetails: newWorkOrder.driver_report_details
            ? {
                ...newWorkOrder.driver_report_details,
                timeOfEvent: newWorkOrder.driver_report_details.timeOfEvent
                  ? moment(newWorkOrder.driver_report_details.timeOfEvent)
                  : null,
              }
            : {
                driverName: '',
                driverNumber: '',
                location: '',
                timeOfEvent: null,
                weightOfVehicle: '',
                driverNotes: '',
                billingThrough: null,
              },
        },
        // lineItems: this.hydrateLineItems(newWorkOrder.work_order_lines),
      });

      // if there's no errors, save the work order lines
      AppStore.addLoading('Saving Work Order Lines...');
      await this.saveWorkOrderLines(newWorkOrder);
      AppStore.removeLoading('Saving Work Order Lines...');

      // WorkOrderStore.addWorkOrderToStore(newWorkOrder.id, newWorkOrder, true);
      PitstopTableCacheStore.deleteKey(`${this.shopId}-work-order-list`);
      WorkOrderStore.clearShopWorkOrdersCache(this.shopId);

      EventBus.$emit('workOrders:refreshTable');

      this.setState({ savingWorkOrder: false });

      if (!this.props.isSlideOutMode) {
        notification.success({
          message: 'Work order saved',
          key: 'orderUpdated',
          description: (
            <Container>
              You can always click on the work order for more details and to
              make edits
            </Container>
          ),
        });
      }

      // only unblock after the save is complete
      this.setState({ hasUnsavedChanges: false }, () => {
        if (this.hasUnsavedChangesRef?.current !== undefined) {
          this.hasUnsavedChangesRef.current = false;
        }
        this.unblock();
        // hide the add part modal
        AppStore.openModals.set(ADD_PART_MODAL, false);

        if (this.props.isSlideOutMode) {
          if (isNewWorkOrder) {
            AppStore.addSuccess('Work order created successfully');

            EventBus.$emit('workOrders:refreshTable');

            EventBus.$emit('dashboardWrapper/UPDATE_SLIDE_OUT', {
              type: 'work-order',
              data: newWorkOrder,
              title: `Work Order #${newWorkOrder.invoice_number}`,
              isNew: false,
            });
          } else {
            AppStore.addSuccess('Work order updated successfully');

            EventBus.$emit('workOrders:updateExpandedWorkOrder', newWorkOrder);

            EventBus.$emit('workOrders:refreshTable');
          }

          // Emit the saved event for other components to listen to
          EventBus.$emit('workOrders:saved', newWorkOrder);

          if (nextBehavior === 'exit') {
            EventBus.$emit('dashboardWrapper/CLOSE_SLIDE_OUT');
          }

          return;
        }

        if (this.queryAssetId) {
          return this.props.history.push(
            `/trailer/${this.queryAssetId}/work-orders`
          );
        }

        if (nextBehavior === 'exit') {
          if (pathname) {
            return this.props.history.push(pathname);
          }
          return this.props.history.push('/work-order');
        }

        if (!this.state.isMobileScreenSize) {
          if (isNewWorkOrder) {
            return this.props.history.push(
              `/work-order/${newWorkOrder.id}/edit`
            );
          } else {
            return this.load();
          }
        }

        const currentSection = this.state.activeTab;
        const lastSection =
          Array.isArray(this.orderedSections) && this.orderedSections.length > 0
            ? this.orderedSections[this.orderedSections.length - 1]
            : null;
        const isLastSection = currentSection === lastSection;
        const nextSection =
          this.orderedSections[
            this.orderedSections.indexOf(currentSection) + 1
          ];

        if (isNewWorkOrder && isLastSection) {
          return this.props.history.push(
            `/work-order/${newWorkOrder.id}/edit?tab=${lastSection}`
          );
        } else if (isNewWorkOrder) {
          return this.props.history.push(
            `/work-order/${newWorkOrder.id}/edit?tab=${nextSection}`
          );
        } else {
          if (!isLastSection) {
            this.setState({ activeTab: nextSection }, () => {
              // update the query param
              this.props.history.push({
                search: `?tab=${this.state.activeTab}`,
              });
            });
          }
          // just reload
          return this.load();
        }
      });
    } catch (error) {
      let friendlyMsg =
        'Sorry, we had some problems to save the Work Order. Try again later';
      Logger.log(error.message);

      if (error && error.errorFields && error.errorFields.length > 0) {
        friendlyMsg = error.errorFields[0]?.errors[0] || friendlyMsg;
      } else if (error && error.message) {
        try {
          const errorObj = JSON.parse(error.message);
          friendlyMsg = _.isArray(errorObj.message)
            ? errorObj.message[0]
            : errorObj.message;
        } catch (jsonError) {
          if (error.message.includes('validateFields')) {
            if (error?.inner && error.inner.errorFields) {
              friendlyMsg =
                error.inner.errorFields[0]?.errors[0] || friendlyMsg;
            } else {
              friendlyMsg = error.message;
            }
          } else {
            friendlyMsg = error.message;
          }
        }

        // customize if friendlyMsg is duplicate key value violates unique constraint
        if (
          friendlyMsg.includes(
            'duplicate key value violates unique constraint "invoice_number_unique"'
          )
        ) {
          friendlyMsg =
            'This work order already exists. Please, try again with a different Work Order Number.';
        }
      }

      notification.error({
        message: 'Oops!',
        key: 'orderSaveError',
        description: <Container>{friendlyMsg}</Container>,
      });
      AppStore.removeLoading('Saving Work Order...');
      AppStore.removeLoading('Saving Work Order Lines...');
      this.setState({ savingWorkOrder: false });
    }
  };

  uploadWorkOrderImages = async () => {
    const fileUrls = [];
    const uFiles = this.state.uploadedImages;

    for (const file of uFiles) {
      const fd = new FormData();
      fd.append('singleInputFileName', file.file);
      try {
        const data = await webServiceProvider.postFormData(
          'dashboard/v1/uploadimage',
          fd
        );
        if (data.fileUrl) {
          fileUrls.push(data.fileUrl);
        }
      } catch (e) {
        console.log(e);
      }
    }

    if (Array.isArray(fileUrls) && fileUrls.length > 0) {
      const newState = [...this.state.wo_images, ...fileUrls];
      this.setState(
        () => ({
          wo_images: newState,
        }),
        () => {
          // console.log('Updated wo_images:', this.state.wo_images);
        }
      );
      return newState;
    }
    return this.state.wo_images;
  };

  uploadWorkOrderFiles = async () => {
    const fileUrls = [];
    const uFiles = this.state.uploadedFiles;

    for (const file of uFiles) {
      const fd = new FormData();
      const fileObj = file.file.originFileObj || file.file;
      fd.append('singleInputFileName', fileObj);
      try {
        const data = await webServiceProvider.postFormData(
          'dashboard/v1/uploadimage',
          fd
        );
        if (data.fileUrl) {
          fileUrls.push(data.fileUrl);
        }
      } catch (e) {
        console.log(e);
      }
    }

    if (Array.isArray(fileUrls) && fileUrls.length > 0) {
      const newState = [...this.state.wo_documents, ...fileUrls];
      this.setState(
        () => ({
          wo_documents: newState,
        }),
        () => {
          // console.log('Updated wo_documents:', this.state.wo_documents);
        }
      );
      return newState;
    }
    return this.state.wo_documents;
  };

  // show modal to add new Part
  showModalToAddPart = (partName) => {
    this.setState({
      newPart: {
        name: '',
        description: partName,
        number: '',
        cost: 0,
        volumeunit: '',
      },
    });
    AppStore.openModals.set(ADD_PART_MODAL, true);
  };

  /**
   * Add a new part to the shop
   * @param {object} payload
   * @param {object} options
   * @return {Promise<object|null>} newPart
   */
  addNewPart = async (
    {
      description = '',
      number = '',
      cost = 0,
      addToInventory = false,
      volumeunit = '',
    },
    { showSuccessAlert = true, hideModal = true, lineItemToAddPart = null }
  ) => {
    const getName = () => (number ? `${number} - ${description}` : description);
    const payload = {
      id_shop: ShopStore.currentShop.id,
      name: getName(),
    };
    // Logger.log('here a new part is attempting to get added');
    const newPart = await WorkOrderStore.createPartOnCustomEndpoint(payload);
    // update the new part with the description and number
    await WorkOrderStore.updatePartOnCustomEndpoint({
      id: newPart.id,
      description,
      number,
      cost,
      volumeunit,
      addToInventory,
    });
    if (showSuccessAlert) {
      notification.success({
        message: 'Part created',
        key: `newPartCreated${newPart.id}`,
        description: (
          <Container data-testid="part-created-notification-description">
            The new part has been added to your account
          </Container>
        ),
      });
    }
    if (hideModal) {
      AppStore.openModals.set(ADD_PART_MODAL, false);
    }
    await this.loadShopParts();
    if (lineItemToAddPart) {
      // find the lineItem with the key (lineItemToAddPart)
      const lineItem = this.state.lineItems.find(
        (item) => item.key === lineItemToAddPart
      );
      if (lineItem) {
        this.onAddPart(lineItem.key, {
          id_vehicle_part: newPart.id,
          is_external_part: false,
          name: getName(),
          markup: 0,
          cost: parseFloat(cost),
        });
        this.deleteSubItem(lineItem.key, 'parts', this.state.partKeyToRemove);
      }
    }

    return newPart;
  };

  onSubmitAddPartModal = async () => {
    try {
      this.setState({
        loadingSavingNewPart: true,
      });
      const {
        description,
        number,
        cost,
        volumeunit = '',
        addToInventory = false,
      } = this.state.newPart;
      await this.addNewPart(
        {
          description,
          number,
          cost,
          volumeunit,
          addToInventory,
        },
        {
          showSuccessAlert: true,
          hideModal: true,
          lineItemToAddPart: this.state.currentLinePartIsBeingAdded,
        }
      );
    } catch (error) {
      let friendlyMsg =
        'Sorry, we had some problems to save the Part. Try again later';
      console.log(error.message);
      if (error && error.message && JSON.parse(error.message)) {
        const errorObj = JSON.parse(error.message);
        friendlyMsg = _.isArray(errorObj.message)
          ? errorObj.message[0]
          : errorObj.message;
      }
      notification.error({
        message: 'Oops!',
        key: 'orderSaveError',
        description: <Container>{friendlyMsg}</Container>,
      });
    } finally {
      this.setState({
        loadingSavingNewPart: false,
      });
    }
  };

  onAddNewPart = async (
    partName = '',
    lineKey = null,
    partKeyToRemove = null
  ) => {
    this.setState({
      currentLinePartIsBeingAdded: lineKey,
      partKeyToRemove: partKeyToRemove,
    });
    this.showModalToAddPart(partName);
  };

  onUpdateLaborItem = (lineItemKey, laborKey, newVal, whatChanged) => {
    const lineItem = this.state.lineItems.find(
      (item) => item.key === lineItemKey
    );
    const labour = lineItem.labours.find((labour) => labour.key === laborKey);
    _.merge(labour, {
      ...labour,
      [whatChanged]: newVal,
    });
    // refresh cost
    const indexOfLineItem = this.state.lineItems.findIndex(
      (item) => item.key === lineItemKey
    );
    const newLineItems = JSON.parse(JSON.stringify(this.state.lineItems));
    lineItem.laborTotal = _.sumBy(lineItem.labours, (labour) => {
      return parseFloat(labour.cost || 0) * parseFloat(labour.hours || 0);
    });
    newLineItems.splice(indexOfLineItem, 1, {
      ...lineItem,
    });
    this.setState({
      lineItems: newLineItems,
    });
  };

  onUpdatePartItem = (lineItemKey, partKey, newVal, changedField) => {
    const costToChange = {};
    if (changedField === 'id_vehicle_part') {
      const shopPart = this.state.shopParts.find((part) => part.id === newVal);
      if (shopPart && shopPart.cost !== null && shopPart.cost !== undefined) {
        const partCost = parseFloat(shopPart.cost);
        costToChange.cost = partCost;
      }
    }
    const lineItem = this.state.lineItems.find(
      (item) => item.key === lineItemKey
    );
    const part = lineItem.parts.find((part) => part.key === partKey);
    _.merge(part, {
      ...part,
      [changedField]: newVal,
      ...costToChange,
    });
    // refresh cost
    const indexOfLineItem = this.state.lineItems.findIndex(
      (item) => item.key === lineItemKey
    );
    const newLineItems = JSON.parse(JSON.stringify(this.state.lineItems));
    // refresh cost
    lineItem.partsTotal = _.sumBy(lineItem.parts, (part) => {
      const cost = part.cost ? parseFloat(part.cost) : 0;
      const markup = part.markup ? parseFloat(part.markup) : 0;
      const amount = part.amount ? parseFloat(part.amount) : 0;
      return (cost + (cost * markup) / 100) * amount;
    });
    newLineItems.splice(indexOfLineItem, 1, {
      ...lineItem,
    });
    this.setState({
      lineItems: newLineItems,
    });
  };

  onUpdateMiscellaneousItem = (lineItemKey, miscKey, newVal, changedField) => {
    this.setState({
      lineItems: this.state.lineItems.map((item) => {
        if (item.key === lineItemKey) {
          return {
            ...item,
            miscellaneous: item.miscellaneous.map((misc) => {
              if (misc.key === miscKey) {
                return {
                  ...misc,
                  [changedField]: newVal,
                };
              }
              return misc;
            }),
          };
        }
        return item;
      }),
    });
  };

  onUpdateDefectItem = (lineItemKey, defectKey, newVal, changedField) => {
    this.setState({
      lineItems: this.state.lineItems.map((item) => {
        if (item.key === lineItemKey) {
          return {
            ...item,
            defects: item.defects.map((defect) => {
              if (defect.key === defectKey) {
                const dvirId =
                  this.state.assetDefects.find(
                    (assetDefect) => assetDefect.id === newVal
                  )?.dvirId || null;
                return {
                  ...defect,
                  [changedField]: newVal,
                  dvirId,
                };
              }
              return defect;
            }),
          };
        }
        return item;
      }),
    });
  };

  addNewLineItem = () => {
    const current = this.state.lineItems ? this.state.lineItems.length : 0;
    const newLineItem = {
      sys: '',
      asy: '',
      comp: '',
      header: `New Work Order Line ${current}`,
      key: uuidv4(),
      // id: uuidv4(),
      title: `New Work Order Line ${current}`,
      description: '',
      laborTotal: 0,
      partsTotal: 0,
      miscTotal: 0,
      laborTotalChangedManually: false,
      partsTotalChangedManually: false,
      miscTotalChangedManually: false,
      labours: [],
      parts: [],
      services: [],
      miscellaneous: [],
      defects: [],
      pms: [],
      expanded: true,
      active: true,
      diagnosis: {
        complaintId: null,
        causeId: null,
        correctionId: null,
      },
      vmrs_full_code: null,
      vmrs_full_description: '',
      vmrs_sys_code: null,
      vmrs_sys_text: '',
      vmrs_asm_code: null,
      vmrs_asm_text: '',
      vmrs_comp_code: null,
      vmrs_full_code_ai: false,
    };

    this.setState((prevState) => ({
      lineItems: [...prevState.lineItems, newLineItem],
    }));
  };

  deleteLineItem = (key) => {
    this.setState((prevState) => ({
      lineItems: prevState.lineItems.filter((item) => item.key !== key),
    }));
    this.handleFormChange();
  };

  updateVMRSCode = (value, key) => {
    this.setState((prevState) => ({
      lineItems: prevState.lineItems.map((item) =>
        item.key === key
          ? {
              ...item,
              vmrs_full_code: value.slice(0, 11),
              vmrs_full_description: value.slice(12),
              vmrs_sys_code: value.slice(0, 3),
              vmrs_asm_code: value.slice(4, 7),
              vmrs_comp_code: value.slice(8, 11),
            }
          : item
      ),
    }));
    this.handleFormChange();
  };

  removeInspectionIssue = (title, description) => {
    this.setState((prevState) => {
      const updatedLineItems = [...prevState.lineItems];

      if (
        !updatedLineItems[0] ||
        !Array.isArray(updatedLineItems[0].inspection_issues)
      ) {
        return null;
      }

      updatedLineItems[0] = {
        ...updatedLineItems[0],
        inspection_issues: updatedLineItems[0].inspection_issues
          .map((group) =>
            group.title === title
              ? {
                  ...group,
                  sections: group.sections.filter((item) => {
                    const [label, desc] = Object.entries(item)[0];
                    return desc !== description;
                  }),
                }
              : group
          )
          .filter((group) => group.sections.length > 0),
      };

      return { lineItems: updatedLineItems };
    });

    this.handleFormChange?.();
  };

  toggleActive = (key) => {
    this.setState((prevState) => ({
      lineItems: prevState.lineItems.map((item) =>
        item.key === key ? { ...item, active: !item.active } : item
      ),
    }));
  };

  moveItem = (lineItemKey, type, updater) => {
    this.setState((prevState) => {
      const updatedLineItems = prevState.lineItems.map((item) => {
        if (item.key !== lineItemKey) return item;

        const originalList = item[type] || [];

        const updatedList =
          typeof updater === 'function' ? updater(originalList) : updater;

        const newList = updatedList.map((subItem, idx) => ({
          ...subItem,
          position: subItem.position ?? idx,
        }));

        const updatedItem = {
          ...item,
          [type]: newList,
        };

        return {
          ...updatedItem,
          ...this.recalculateTotals(updatedItem, type),
        };
      });

      return { lineItems: updatedLineItems };
    }, this.handleFormChange);
  };

  addSubItem = (key, type, values) => {
    this.setState((prevState) => {
      const updatedItems = prevState.lineItems.map((item) => {
        if (item.key === key) {
          const updatedItem = {
            ...item,
            [type]: [...(item[type] || []), values],
          };

          if (type === 'services') {
            updatedItem.showServices = true;
          } else if (type === 'defects') {
            updatedItem.showDefects = true;
          } else if (type === 'pms') {
            updatedItem.showPms = true;
          }

          return {
            ...updatedItem,
            ...this.recalculateTotals(updatedItem, type),
          };
        }
        return item;
      });

      return { lineItems: updatedItems };
    });

    this.handleFormChange();
  };

  deleteSubItem = (key, type, id) => {
    this.setState((prevState) => {
      const index = prevState.lineItems.findIndex((item) => item.key === key);
      if (index === -1) return null;

      const item = prevState.lineItems[index];
      const filteredList = id
        ? item[type].filter((subItem) => subItem.key !== id)
        : [];

      const updatedItem = {
        ...item,
        [type]: filteredList,
        ...this.recalculateTotals({ ...item, [type]: filteredList }, type),
      };

      if (type === 'services') {
        updatedItem.showServices = false;
      } else if (type === 'defects') {
        updatedItem.showDefects = false;
      } else if (type === 'pms') {
        updatedItem.showPms = false;
      }

      const updatedItems = [...prevState.lineItems];
      updatedItems[index] = updatedItem;

      return { lineItems: updatedItems };
    }, this.handleFormChange);
  };

  changeSubItem = (key, type, id, value) => {
    this.setState((prevState) => {
      const updatedItems = prevState.lineItems.map((item) => {
        if (item.key !== key) return item;

        const updatedList = item[type].map((subItem) =>
          subItem.key === id ? { ...subItem, ...value } : subItem
        );

        const updatedItem = { ...item, [type]: updatedList };

        return {
          ...updatedItem,
          ...this.recalculateTotals(updatedItem, type),
        };
      });

      return { lineItems: updatedItems };
    }, this.handleFormChange);
  };

  changeItemProperty = (key, property, value) => {
    this.setState((prevState) => {
      const updatedItems = prevState.lineItems.map((item) =>
        item.key === key
          ? {
              ...item,
              [property]: value,
            }
          : item
      );

      return { lineItems: updatedItems };
    });
    this.handleFormChange();
  };

  recalculateTotals = (item, type) => {
    let laborTotal, partsTotal, miscTotal;

    if (!!item.laborTotalChangedManually === false && type === 'labours') {
      laborTotal = item.labours.reduce(
        (acc, labour) => acc + labour.cost * labour.hours,
        0
      );
    } else {
      laborTotal = item.laborTotal;
    }

    if (!!item.partsTotalChangedManually === false && type === 'parts') {
      partsTotal = item.parts.reduce(
        (acc, part) =>
          acc + (part.cost + (part.cost * part.markup) / 100) * part.amount,
        0
      );
    } else {
      partsTotal = item.partsTotal;
    }

    if (!!item.miscTotalChangedManually === false && type === 'miscellaneous') {
      miscTotal = item.miscellaneous.reduce(
        (acc, misc) => acc + misc.unit_cost * misc.quantity,
        0
      );
    } else {
      miscTotal = item.miscTotal;
    }

    return {
      laborTotal: parseFloat(laborTotal.toFixed(2)) || 0,
      partsTotal: parseFloat(partsTotal.toFixed(2)) || 0,
      miscTotal: parseFloat(miscTotal.toFixed(2)) || 0,
    };
  };

  onSelectSystemCode = (lineItemKey, systemCode) => {
    const lineItem = this.state.lineItems.find(
      (item) => item.key === lineItemKey
    );
    lineItem.vmrs_sys_code_id = systemCode;
    lineItem.vmrs_sys_code = null;
    lineItem.vmrs_sys_text = null;
    this.setState({
      lineItems: this.state.lineItems,
    });
  };

  onSelectAsmCode = (lineItemKey, asmCode) => {
    const lineItem = this.state.lineItems.find(
      (item) => item.key === lineItemKey
    );
    lineItem.vmrs_asm_code_id = asmCode;
    lineItem.vmrs_asm_code = null;
    lineItem.vmrs_asm_text = null;
    this.setState({
      lineItems: this.state.lineItems,
    });
  };

  onUpdateServiceItem = (lineItemKey, serviceKey, newVal) => {
    // Logger.log('onUpdateServiceItem is being run');
    const lineItem = this.state.lineItems.find(
      (item) => item.key === lineItemKey
    );
    const service = lineItem.services.find(
      (service) => service.key === serviceKey
    );
    _.merge(service, newVal);
  };

  onChangeCollapseItems = (activeLineItemsKeys) => {
    const { lineItems } = this.state;
    lineItems.forEach((lineItem) => {
      if (activeLineItemsKeys.includes(`${lineItem.key}`)) {
        lineItem.expanded = true;
      } else {
        lineItem.expanded = false;
      }
    });
    this.forceUpdate();
  };

  onGetCarDetails = (carDetails) => {
    this.setState(
      {
        carDetails,
      },
      () => {
        this.loadAssetDVIRs(carDetails);
      }
    );
  };

  handleExportClick = async (type) => {
    // Get work order ID from route params or from loaded work order data (slide-out mode)
    const workOrderId =
      this.props.match.params.id || this.state.currentWorkOrder?.id;

    switch (type) {
      case 'print':
        try {
          AppStore.addLoading('Getting Work Order Report');
          await WorkOrderStore.printWorkOrder(workOrderId);
        } catch (error) {
          console.error(error);
          AppStore.addError(
            'Unknown error to generate the Work Order. Please try again later'
          );
        } finally {
          AppStore.removeLoading('Getting Work Order Report');
        }
        break;
      case 'download':
        try {
          AppStore.addLoading('Getting Work Order Report');
          await WorkOrderStore.downloadWorkOrder(workOrderId);
        } catch (error) {
          console.error(error);
          AppStore.addError(
            'Unknown error to generate the Work Order. Please try again later'
          );
        } finally {
          AppStore.removeLoading('Getting Work Order Report');
        }
        break;
      default:
        break;
    }
  };

  handleFileUpload = (file, additionalData) => {
    // console.log('Before update wo_documents:', this.state.wo_documents);
    this.setState(
      (prevState) => {
        let updatedDocuments = prevState.uploadedFiles;
        let woDocumentsUpdated = prevState.wo_documents;

        if (additionalData === 'removed') {
          updatedDocuments = prevState.uploadedFiles.filter(
            (item) => item.file.uid !== file.uid || item.file.name !== file.name
          );
          woDocumentsUpdated = prevState.wo_documents.filter(
            (documentUrl) => documentUrl !== file.url
          );
        } else {
          updatedDocuments = [
            ...prevState.uploadedFiles,
            { file, additionalData },
          ];
        }

        let scannedWorkOrderPreview = prevState.scannedWorkOrderPreview;
        if (additionalData === 'removed') {
          scannedWorkOrderPreview = null;
        }

        return {
          uploadedFiles: updatedDocuments,
          wo_documents: woDocumentsUpdated,
          scannedWorkOrderPreview,
        };
      },
      () => {
        // console.log(
        //   'Updated state after file upload:',
        //   this.state.uploadedFiles
        // );
        // console.log('Updated wo_documents:', this.state.wo_documents);
      }
    );
  };

  handleImageUpload = (file, additionalData) => {
    // console.log('Before update wo_images:', this.state.wo_images);
    this.setState(
      (prevState) => {
        let updatedImages = prevState.uploadedImages;
        let woImagesUpdated = prevState.wo_images;

        if (additionalData === 'removed') {
          // Remove file from uploadedImages based on uid and name
          updatedImages = prevState.uploadedImages.filter(
            (item) => item.file.uid !== file.uid || item.file.name !== file.name
          );
          woImagesUpdated = prevState.wo_images.filter(
            (imageUrl) => imageUrl !== file.url
          );
        } else {
          updatedImages = [
            ...prevState.uploadedImages,
            { file, additionalData },
          ];
        }

        return {
          uploadedImages: updatedImages,
          wo_images: woImagesUpdated,
        };
      },
      () => {
        // console.log(
        //   'Updated state after image upload:',
        //   this.state.uploadedImages
        // );
        // console.log('Updated wo_images:', this.state.wo_images);
      }
    );
  };

  addNewShopUser = async ({ name = '', email = '', role = 'dealership' }) => {
    if (
      !ShopStore.currentShop.id ||
      ShopStore.currentShop.id === -1 ||
      ShopStore.currentShop.id === '-1'
    ) {
      return;
    }
    try {
      const userAdded = await UserStore.addUserToShop({
        shopId: ShopStore.currentShop.id,
        firstName: name,
        role: role,
        shopRole: 'fleetManager',
        email: email,
        username: email,
        password: Math.random().toString(36).slice(-8),
        isSendCredentialsByPhone: false,
      });
      return userAdded;
    } catch (error) {
      Logger.error(error);
      let message = error.message;
      if (
        String(error?.message).indexOf('is already used') &&
        error?.message.includes('@username.pitstop')
      ) {
        // get the first part of the email
        const email = error?.message
          .split(' ')
          .find((part) => part.includes('@username.pitstop'));
        const username = email.split('@')[0];
        message = `The username ${username} is already in use. Please try another one.`;
      }
      AppStore.addError(message);
    }
  };

  loadWorkOrderWithExtractedInformation = async (data, options = {}) => {
    const {
      stringify = true,
      fillWorkOrderNumber = false,
      fromSlideout = false,
    } = options;
    // Logger.log('we are now attempting to fill in line items with json');
    this.setState({
      importedInformation: stringify ? JSON.stringify(data, null, 4) : data,
    });

    // if (fromSlideout) return; now we need it to do stuff not just return.

    const clearStr = (str) => deburr(kebabCase(str)).toLowerCase();
    const isFound = (str) => str && clearStr(str) !== 'not-found';
    try {
      // Logger.log('loading work order supposedly');
      AppStore.addLoading('Loading Work Order');
      const extractedInitialFormValues = {
        woRepairType: this.state.initialFormValues.woRepairType,
        woStatus: this.state.initialFormValues.woStatus,
      };
      // wo #
      if (fillWorkOrderNumber) {
        if (isFound(data['Invoice Number'])) {
          extractedInitialFormValues.woInvoiceNumber = data['Invoice Number'];
        } else if (isFound(data['Work Order Number'])) {
          extractedInitialFormValues.woInvoiceNumber =
            data['Work Order Number'];
        }
      }
      // start date
      if (
        isFound(data['Service Date']) &&
        !this.state.initialFormValues.woStartDate
      ) {
        if (moment(data['Service Date'], 'MM/DD/YYYY').isValid()) {
          extractedInitialFormValues.woStartDate = moment(
            data['Service Date'],
            'MM/DD/YYYY'
          );
        } else if (moment(data['Service Date'], 'YYYY-MM-DD').isValid()) {
          extractedInitialFormValues.woStartDate = moment(
            data['Service Date'],
            'YYYY-MM-DD'
          );
        }
      }
      // vehicle
      if (
        isFound(data['Vehicle Identifier']) &&
        !this.state.initialFormValues.woSelectVehicle
      ) {
        const { result: vehicle } = await CarStore.getShopCarByVin(
          ShopStore.currentShop.id,
          data['Vehicle Identifier']
        );
        if (Array.isArray(vehicle) && vehicle.length) {
          extractedInitialFormValues.woSelectVehicle = vehicle[0].id;
        } else {
          extractedInitialFormValues.extractedVin = data['Vehicle Identifier'];
        }
      }
      // Tax
      if (
        isFound(data['Tax']) &&
        (!this.state.initialFormValues.tax ||
          !this.state.initialFormValues.tax.value)
      ) {
        extractedInitialFormValues.tax = {
          type: 'absolute',
          value: parseFloat(data['Tax']),
        };
      }
      // Service Line Items
      const lineItems = [];
      // Check if this is the version that supports nested line items
      if (
        Object.prototype.hasOwnProperty.call(data, 'Services') &&
        isArray(data['Services']) &&
        !isEmpty(data['Services'])
      ) {
        const serviceItems = data['Services'];
        for (let sIdx = 0; sIdx < serviceItems.length; sIdx++) {
          const service = serviceItems[sIdx] || {};
          const lineItem = {
            key: (lineItems?.length || 0) + 1,
            title: isFound(service['service_title'])
              ? service['service_title']
              : `Service ${(lineItems?.length || 0) + 1}`,
            description: service['service_notes'] || '',
            labour_cost: 0,
            parts_cost: 0,
            parts: [],
            labours: [],
            miscellaneous: [],
            defects: [],
          };

          const serviceLineItems = service['line_items'];
          if (isArray(serviceLineItems) && !isEmpty(serviceLineItems)) {
            for (const item of serviceLineItems) {
              const explicitType = isFound(item['type'])
                ? String(item['type']).trim().toLowerCase()
                : null;
              const legacyType = isFound(item['parts_labor_or_other'])
                ? String(item['parts_labor_or_other']).trim().toLowerCase()
                : null;
              const itemType = explicitType || legacyType;

              if (itemType === 'part' || itemType === 'parts') {
                const partName = item['description'];
                const partCost = isFound(item['unit_cost'])
                  ? parseFloat(parseFloat(item['unit_cost']).toFixed(2))
                  : 0;
                const partNumber = isFound(item['part_number'])
                  ? item['part_number']
                  : '';
                const partMarkup = isFound(item['markup'])
                  ? parseFloat(item['markup'])
                  : 0;
                const partQuantity = isFound(item['quantity'])
                  ? parseFloat(item['quantity'])
                  : 0;

                let foundPart = this.state.shopParts.find((shopPart) => {
                  return (
                    clearStr(shopPart.name) ===
                    clearStr(`${partNumber} - ${partName}`)
                  );
                });
                if (!foundPart) {
                  foundPart = await this.addNewPart(
                    {
                      description: partName,
                      number: partNumber || '',
                      cost: partCost,
                      addToInventory: false,
                    },
                    {
                      showSuccessAlert: false,
                      hideModal: false,
                      lineItemToAddPart: null,
                    }
                  );
                }
                lineItem.parts.push({
                  key:
                    (Array.isArray(lineItem?.parts)
                      ? lineItem.parts.length
                      : 0) + 1,
                  id_vehicle_part: foundPart.id,
                  is_external_part: false,
                  name: foundPart.name,
                  amount: partQuantity,
                  originalAmount: partQuantity,
                  markup: partMarkup,
                  cost: partCost,
                });
              } else if (itemType === 'labor' || itemType === 'labour') {
                const labourName = item['technician_name'];
                const labourCost = isFound(item['unit_cost'])
                  ? parseFloat(item['unit_cost'])
                  : 0;
                const labourQuantity = isFound(item['quantity'])
                  ? parseFloat(item['quantity'])
                  : 0;
                const labourDescription = item['description'];

                let foundUser = this.shopUsers.find(
                  (user) =>
                    user.name &&
                    user.name.trim().toLowerCase() ===
                      String(labourName || '')
                        .trim()
                        .toLowerCase()
                );

                lineItem.labours.push({
                  key:
                    (Array.isArray(lineItem?.labours)
                      ? lineItem.labours.length
                      : 0) + 1,
                  id_labour: foundUser ? foundUser.id : null,
                  name: labourName,
                  description: labourDescription,
                  hours: labourQuantity,
                  cost: labourCost,
                });
              } else if (itemType === 'other') {
                const otherDescription = item['description'];
                const otherCost = isFound(item['unit_cost'])
                  ? parseFloat(item['unit_cost'])
                  : 0;
                const otherQuantity = isFound(item['quantity'])
                  ? parseFloat(item['quantity'])
                  : 0;
                lineItem.miscellaneous.push({
                  key:
                    (Array.isArray(lineItem?.miscellaneous)
                      ? lineItem.miscellaneous.length
                      : 0) + 1,
                  description: otherDescription,
                  quantity: otherQuantity,
                  unit_cost: otherCost,
                });
              }
            }
          }

          lineItem.parts_cost = _.sumBy(lineItem.parts, (part) => {
            const cost = part.cost ? parseFloat(part.cost) : 0;
            const markup = part.markup ? parseFloat(part.markup) : 0;
            const amount = part.amount ? parseFloat(part.amount) : 0;
            return (cost + (cost * markup) / 100) * amount;
          });

          lineItem.labour_cost = _.sumBy(lineItem.labours, (labour) => {
            return parseFloat(labour.cost || 0) * parseFloat(labour.hours || 0);
          });
          lineItem.labour_cost = parseFloat(
            parseFloat(lineItem.labour_cost).toFixed(2)
          );

          lineItem.miscellaneous_cost = _.sumBy(
            lineItem.miscellaneous,
            (misc) => {
              return (
                parseFloat(misc.unit_cost || 0) * parseFloat(misc.quantity || 0)
              );
            }
          );

          lineItems.push(lineItem);
        }

        if (
          Object.prototype.hasOwnProperty.call(data, 'Other Line Items') &&
          isArray(data['Other Line Items']) &&
          !isEmpty(data['Other Line Items'])
        ) {
          const otherItems = data['Other Line Items'];
          const otherLineItem = {
            key: (lineItems?.length || 0) + 1,
            title: 'Other Line Items',
            description: '',
            labour_cost: 0,
            parts_cost: 0,
            parts: [],
            labours: [],
            miscellaneous: [],
            defects: [],
          };

          for (const other of otherItems) {
            const otherDescription = other['description'];
            const otherCost = isFound(other['unit_cost'])
              ? parseFloat(other['unit_cost'])
              : 0;
            const otherQuantity = isFound(other['quantity'])
              ? parseFloat(other['quantity'])
              : 0;
            otherLineItem.miscellaneous.push({
              key:
                (Array.isArray(otherLineItem?.miscellaneous)
                  ? otherLineItem.miscellaneous.length
                  : 0) + 1,
              description: otherDescription,
              quantity: otherQuantity,
              unit_cost: otherCost,
            });
          }

          otherLineItem.miscellaneous_cost = _.sumBy(
            otherLineItem.miscellaneous,
            (misc) => {
              return (
                parseFloat(misc.unit_cost || 0) * parseFloat(misc.quantity || 0)
              );
            }
          );

          lineItems.push(otherLineItem);
        }
      } else if (
        isArray(data['Service Line Items']) &&
        !isEmpty(data['Service Line Items'])
      ) {
        const serviceLineItems = data['Service Line Items'];
        // Check if version 2 scan
        if (
          Object.prototype.hasOwnProperty.call(
            first(serviceLineItems),
            'parts_labor_or_other'
          )
        ) {
          // get parts
          const parts = serviceLineItems.filter(
            (item) =>
              String(item['parts_labor_or_other']).trim().toLowerCase() ===
              'parts'
          );
          // get labours
          const labours = serviceLineItems.filter(
            (item) =>
              String(item['parts_labor_or_other']).trim().toLowerCase() ===
              'labor'
          );
          // get others
          const others = serviceLineItems.filter(
            (item) =>
              String(item['parts_labor_or_other']).trim().toLowerCase() ===
              'other'
          );
          const lineItem = {
            key: (lineItems?.length || 0) + 1,
            title: `Service Line Item ${(lineItems?.length || 0) + 1}`,
            description: '',
            labour_cost: 0,
            parts_cost: 0,
            parts: [],
            labours: [],
            miscellaneous: [],
            defects: [],
          };
          for (const part of parts) {
            const partName = part['description'];
            const partCost = isFound(part['unit_cost'])
              ? parseFloat(parseFloat(part['unit_cost']).toFixed(2))
              : 0;
            const partNumber = isFound(part['part_number'])
              ? part['part_number']
              : '';
            const partMarkup = isFound(part['markup'])
              ? parseFloat(part['markup'])
              : 0;
            const partQuantity = isFound(part['quantity'])
              ? parseFloat(part['quantity'])
              : 0;
            // search that part name in the shop/subshop parts
            let foundPart = this.state.shopParts.find((shopPart) => {
              return (
                clearStr(shopPart.name) ===
                clearStr(`${partNumber} - ${partName}`)
              );
            });
            if (!foundPart) {
              // if not found, create a new part
              foundPart = await this.addNewPart(
                {
                  description: partName,
                  number: partNumber || '',
                  cost: partCost,
                  addToInventory: false,
                },
                {
                  showSuccessAlert: false,
                  hideModal: false,
                  lineItemToAddPart: null,
                }
              );
            }
            lineItem.parts.push({
              key:
                (Array.isArray(lineItem?.parts) ? lineItem.parts.length : 0) +
                1,
              id_vehicle_part: foundPart.id,
              is_external_part: false,
              name: foundPart.name,
              amount: partQuantity,
              originalAmount: partQuantity,
              markup: partMarkup,
              cost: partCost,
            });
          }

          for (const labour of labours) {
            const labourName = labour['technician_name'];
            const labourCost = isFound(labour['unit_cost'])
              ? parseFloat(labour['unit_cost'])
              : 0;
            const labourQuantity = isFound(labour['quantity'])
              ? parseFloat(labour['quantity'])
              : 0;
            const labourDescription = labour['description'];

            let foundUser = this.shopUsers.find(
              (user) =>
                user.name &&
                user.name.trim().toLowerCase() ===
                  String(labourName).trim().toLowerCase()
            );

            lineItem.labours.push({
              key:
                (Array.isArray(lineItem?.labours)
                  ? lineItem.labours.length
                  : 0) + 1,
              id_labour: foundUser ? foundUser.id : null,
              name: labourName,
              description: labourDescription,
              hours: labourQuantity,
              cost: labourCost,
            });
          }

          for (const other of others) {
            const otherDescription = other['description'];
            const otherCost = isFound(other['unit_cost'])
              ? parseFloat(other['unit_cost'])
              : 0;
            const otherQuantity = isFound(other['quantity'])
              ? parseFloat(other['quantity'])
              : 0;
            lineItem.miscellaneous.push({
              key:
                (Array.isArray(lineItem?.miscellaneous)
                  ? lineItem.parts.miscellaneous
                  : 0) + 1,
              description: otherDescription,
              quantity: otherQuantity,
              unit_cost: otherCost,
            });
          }

          // refresh parts total cost
          lineItem.parts_cost = _.sumBy(lineItem.parts, (part) => {
            const cost = part.cost ? parseFloat(part.cost) : 0;
            const markup = part.markup ? parseFloat(part.markup) : 0;
            const amount = part.amount ? parseFloat(part.amount) : 0;
            return (cost + (cost * markup) / 100) * amount;
          });

          // refresh labour total cost
          lineItem.labour_cost = _.sumBy(lineItem.labours, (labour) => {
            return parseFloat(labour.cost || 0) * parseFloat(labour.hours || 0);
          });
          // always round the labour_cost to 2 decimal places
          lineItem.labour_cost = parseFloat(
            parseFloat(lineItem.labour_cost).toFixed(2)
          );

          // refresh miscellaneous total cost
          lineItem.miscellaneous_cost = _.sumBy(
            lineItem.miscellaneous,
            (misc) => {
              return (
                parseFloat(misc.unit_cost || 0) * parseFloat(misc.quantity || 0)
              );
            }
          );

          // push the lineItem before handling parts
          lineItems.push(lineItem);
        } else {
          // Version 1 Scan
          for (const serviceLineItem of serviceLineItems) {
            if (isFound(serviceLineItem['name'])) {
              const lineItem = {
                key: (lineItems?.length || 0) + 1,
                title: serviceLineItem['name'],
                description: serviceLineItem['description'] || '',
                labour_cost: 0,
                parts_cost: 0,
                parts: [],
                labours: [],
              };
              // check if has parts and add it too
              if (
                serviceLineItem['parts'] &&
                isArray(serviceLineItem['parts'])
              ) {
                // for each part, search that part based on it's name. If not found, create one and get the data back.
                for (const part of serviceLineItem['parts']) {
                  if (!isFound(part['name'])) {
                    // if the part name is not found, skip it
                    continue;
                  }
                  const partName = part['name'];
                  const partCost = isFound(part['unit_cost'])
                    ? parseFloat(part['unit_cost'])
                    : 0;
                  const partNumber = isFound(part['part_number'])
                    ? part['part_number']
                    : '';
                  const partMarkup = isFound(part['mark_up'])
                    ? parseFloat(part['mark_up'])
                    : 0;
                  const partQuantity = isFound(part['quantity'])
                    ? parseFloat(part['quantity'])
                    : 0;
                  // search that part name in the shop/subshop parts
                  let foundPart = this.state.shopParts.find((shopPart) => {
                    return (
                      clearStr(shopPart.name) ===
                      clearStr(`${partNumber} - ${partName}`)
                    );
                  });
                  if (!foundPart) {
                    // if not found, create a new part
                    foundPart = await this.addNewPart(
                      {
                        description: partName,
                        number: partNumber || '',
                        cost: partCost,
                        addToInventory: false,
                      },
                      {
                        showSuccessAlert: false,
                        hideModal: false,
                        lineItemToAddPart: null,
                      }
                    );
                  }
                  lineItem.parts.push({
                    key:
                      (Array.isArray(lineItem?.parts)
                        ? lineItem.parts.length
                        : 0) + 1,
                    id_vehicle_part: foundPart.id,
                    is_external_part: false,
                    name: foundPart.name,
                    amount: partQuantity,
                    originalAmount: partQuantity,
                    markup: partMarkup,
                    cost: partCost,
                  });
                }
              }

              // refresh parts total cost
              lineItem.parts_cost = _.sumBy(lineItem.parts, (part) => {
                const cost = part.cost ? parseFloat(part.cost) : 0;
                const markup = part.markup ? parseFloat(part.markup) : 0;
                const amount = part.amount ? parseFloat(part.amount) : 0;
                return (cost + (cost * markup) / 100) * amount;
              });

              // push the lineItem before handling parts
              lineItems.push(lineItem);
            }
          }
        }
      }

      const hydratedLineItems = this.hydrateLineItems(lineItems);
      let fillData = [...this.state.lineItems];

      hydratedLineItems.forEach((ocrItem) => {
        let targetIdx = fillData.findIndex(
          (item) => !(item.diagnosis && item.diagnosis.ocrImported)
        );
        if (targetIdx === -1)
          targetIdx = Array.isArray(fillData) ? fillData.length : 0;

        if (fillData[targetIdx]) {
          const mergedParts = [
            ...(fillData[targetIdx].parts || []),
            ...(ocrItem.parts || []),
          ];
          const mergedLabours = [
            ...(fillData[targetIdx].labours || []),
            ...(ocrItem.labours || []),
          ];
          const mergedMisc = [
            ...(fillData[targetIdx].miscellaneous || []),
            ...(ocrItem.miscellaneous || []),
          ];

          const mergedLineItem = {
            ...fillData[targetIdx],
            ...ocrItem,
            diagnosis: {
              ...(fillData[targetIdx].diagnosis || {}),
              ocrImported: true,
            },
            parts: mergedParts,
            labours: mergedLabours,
            miscellaneous: mergedMisc,
          };

          const partsTotals = this.recalculateTotals(mergedLineItem, 'parts');
          const laboursTotals = this.recalculateTotals(
            mergedLineItem,
            'labours'
          );
          const miscTotals = this.recalculateTotals(
            mergedLineItem,
            'miscellaneous'
          );

          fillData[targetIdx] = {
            ...mergedLineItem,
            partsTotal: partsTotals.partsTotal ?? 0,
            laborTotal: laboursTotals.laborTotal ?? 0,
            miscTotal: miscTotals.miscTotal ?? 0,
          };
        } else {
          const partsTotals = this.recalculateTotals(ocrItem, 'parts');
          const laboursTotals = this.recalculateTotals(ocrItem, 'labours');
          const miscTotals = this.recalculateTotals(ocrItem, 'miscellaneous');

          fillData.push({
            ...ocrItem,
            key: uuidv4(),
            diagnosis: { ...(ocrItem.diagnosis || {}), ocrImported: true },
            partsTotal: partsTotals.partsTotal ?? 0,
            laborTotal: laboursTotals.laborTotal ?? 0,
            miscTotal: miscTotals.miscTotal ?? 0,
          });
        }
      });

      if (fillWorkOrderNumber) {
        this.workOrderFormRef.setFieldsValue({
          woInvoiceNumber: extractedInitialFormValues.woInvoiceNumber,
        });
      }
      this.setState({
        lineItems: fillData,
        initialFormValues: {
          ...this.state.initialFormValues,
          ...extractedInitialFormValues,
        },
      });
    } catch (error) {
      Logger.error(error);
    } finally {
      AppStore.removeLoading('Loading Work Order');
    }
  };

  handleWorkOrderUpload = async (fileList) => {
    if (!fileList?.length) {
      return;
    }
    await this.setState({ isUploadCancelled: false });
    // console.log('Handling work order upload with fileList:', fileList);
    for (const file of fileList[0]) {
      if (this.state.isUploadCancelled) break;
      // console.log('Processing file:', file);
      try {
        this.setState({
          uploading: true,
          scanDone: false,
          isErrorWorkOrderUpload: false,
          // progress: 0,
          currentStep: 1,
        });

        const progress = this.progressDuringOCR();

        const firstFile = file.originFileObj;
        this.setState({ currentUploadingFile: firstFile });
        // pass the first file to the ImageUploadContainer, only if it's an image
        if (
          firstFile.type.includes('image') &&
          this.imageUploaderContainerRef &&
          this.imageUploaderContainerRef.handlePreview
        ) {
          this.imageUploaderContainerRef.handlePreview(firstFile);
        } else if (
          this.documentUploaderContainerRef &&
          this.documentUploaderContainerRef.setFileList
        ) {
          const currentList =
            this.documentUploaderContainerRef.getFileList() || [];
          this.documentUploaderContainerRef.setFileList([...currentList, file]);
          this.handleFileUpload(firstFile, 'added');
        }

        // Create blob URL for preview instead of base64
        const blobUrl = URL.createObjectURL(firstFile);

        this.setState({ scannedWorkOrderPreview: blobUrl });
        // scroll to the top
        this.scrollToTopAndSaveOriginalMediaPreviewData();

        // Convert to base64 for OCR API call
        const base64 = await this.readFileAsDataURL(firstFile);
        const withoutHeaders = base64.split(',')[1];
        // upload the base64 to the server
        try {
          const woOcrAbortController = new AbortController();
          this.setState({
            isLoadingWorkOrderUpload: true,
            // progress: 75,
            currentStep: 2,
            workOrderImgOcrSignal: woOcrAbortController,
          });

          const data = await WorkOrderStore.workOrderImgOcr(
            {
              base64: withoutHeaders,
              shopId: ShopStore.currentShop.id,
              fileType: firstFile.type,
            },
            woOcrAbortController.signal
          );

          // this.setState(
          //   // { progress: 90 },
          //   () => {
          //   }
          // );
          this.setState({ scanDone: true });
          this.loadWorkOrderWithExtractedInformation(data, {
            stringify: true,
            fillWorkOrderNumber:
              customFleets.pfgFleet.includes(
                ShopStore.currentShop.parentShopId || ShopStore.currentShop.id
              ) || ShopStore.currentShop.id === 1157
                ? true
                : false,
          });

          await progress;
        } catch (error) {
          if (error.name === 'AbortError') {
            Logger.log('OCR request aborted');
            this.setState({
              uploading: false,
              // progress: 0,
              currentStep: 0,
              isErrorWorkOrderUpload: false,
            });
          } else {
            Logger.log('Error:', error);
            this.setState({
              uploading: false,
              // progress: 0,
              currentStep: 0,
              isErrorWorkOrderUpload: true,
            });
          }
        } finally {
          this.setState({
            isLoadingWorkOrderUpload: false,
            uploading: false,
            // progress: 0,
            currentStep: 0,
          });
        }
        // };
      } catch (error) {
        Logger.error(error);
      }
    }
    this.setState({
      isUploadWorkOrderModalVisible: false,
      uploading: false,
      scanQueue: [],
      currentStep: 0,
      scanDone: false,
    });
  };

  readFileAsDataURL(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
    });
  }

  progressDuringOCR = async () => {
    const steps = [
      { step: 1, delay: 2000 },
      { step: 2, delay: 7500 },
      { step: 3, delay: 6400 },
      { step: 4, delay: 2900 },
      { step: 5, delay: 7100 },
      { step: 6, delay: 14300 },
    ];

    for (const { step, delay } of steps) {
      if (this.state.isErrorWorkOrderUpload || this.state.isUploadCancelled)
        return;

      if (this.state.scanDone) break;
      if (this.state.currentStep < step) this.setState({ currentStep: step });

      const interval = 100;
      let waited = 0;
      while (waited < delay) {
        if (this.state.scanDone) break;
        if (this.state.isErrorWorkOrderUpload || this.state.isUploadCancelled)
          return;
        await new Promise((resolve) => setTimeout(resolve, interval));
        waited += interval;
      }
      if (this.state.scanDone) break;
    }
    this.setState({ currentStep: 7 });
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  get vendorOptions() {
    return (this.state.shopVendors || []).map((user) => (
      <Select.Option key={user.id} value={user.id}>
        {user.name}
      </Select.Option>
    ));
  }

  setInitialFormValue = (key, value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        [key]: value,
      },
    });
  };

  setCurrentWorkOrderValue = (key, value) => {
    this.setState({
      currentWorkOrder: {
        ...this.state.currentWorkOrder,
        [key]: value,
      },
    });
  };

  getRandomKey = (arr = []) => {
    const newKey = _.random(10000);
    if (arr.some((a) => a.key === newKey)) {
      return this.getRandomKey(arr);
    }
    return newKey;
  };

  onAddLabor = (lineItemKey) => {
    const currentUserSettings = CurrentUserStore.user.settings;
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          lineItem.labours.push({
            key: this.getRandomKey(lineItem.labours),
            id_labour: null,
            hours: 0,
            cost: currentUserSettings?.laborRate || 0,
          });
        }
        return lineItem;
      }),
    });
  };

  onRemoveLabor = (laborKey, lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          lineItem.labours = lineItem.labours.filter(
            (labour) => labour.key !== laborKey
          );
        }
        return lineItem;
      }),
    });
  };

  onRemovePart = (partKey, lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          lineItem.parts = lineItem.parts.filter(
            (part) => part.key !== partKey
          );
        }
        return lineItem;
      }),
    });
  };

  onRemoveMiscellaneous = (miscKey, lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          lineItem.miscellaneous = lineItem.miscellaneous.filter(
            (misc) => misc.key !== miscKey
          );
        }
        return lineItem;
      }),
    });
  };

  onRemoveDefect = (defectKey, lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          lineItem.defects = lineItem.defects.filter(
            (defect) => defect.key !== defectKey
          );
        }
        return lineItem;
      }),
    });
  };

  onRemoveIssue = (issueKey, lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          lineItem.services = lineItem.services.filter(
            (issue) => issue.key !== issueKey
          );
        }
        return lineItem;
      }),
    });
  };

  onAddPart = (lineItemKey, existingPart = {}) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          const partKey = this.getRandomKey(lineItem.parts);
          lineItem.parts.push({
            key: partKey,
            id_vehicle_part: null,
            is_external_part: false,
            name: `Part ${partKey}`,
            amount: 1,
            originalAmount: 1,
            markup: 0,
            cost: 0,
            ...existingPart,
          });
        }
        return lineItem;
      }),
    });
  };

  onAddIssue = (lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          const issueKey = this.getRandomKey(lineItem.services);
          lineItem.services.push({
            key: issueKey,
            id_vehicle_service: '',
            name: `Issue ${issueKey}`,
          });
        }
        return lineItem;
      }),
    });
  };

  onAddMiscellaneous = (lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          const miscKey = this.getRandomKey(lineItem.miscellaneous);
          lineItem.miscellaneous.push({
            key: miscKey,
            description: '',
            quantity: 1,
            unit_cost: 0,
          });
        }
        return lineItem;
      }),
    });
  };

  onAddDefect = (lineItemKey) => {
    this.setState({
      lineItems: this.state.lineItems.map((lineItem) => {
        if (lineItem.key === lineItemKey) {
          if (!lineItem.defects) {
            lineItem.defects = [];
          }
          const defectKey = this.getRandomKey(lineItem.defects);
          lineItem.defects.push({
            key: defectKey,
            id_defect: null,
            name: `Defect ${defectKey}`,
            isResolved: false,
            comment: '',
            dvirId: null,
          });
        }
        return lineItem;
      }),
    });
  };

  onChangeSummaryTotal = (value) => {
    this.setState({
      costsTotalHasManualOverride: true,
      manualOverrides: {
        ...this.state.manualOverrides,
        manualCostsTotal: value,
      },
    });
  };

  onResetCostsTotalOverride = () => {
    this.setState({
      costsTotalHasManualOverride: false,
      manualOverrides: {
        ...this.state.manualOverrides,
        manualCostsTotal: 0,
      },
    });
  };

  onChangeLabourTotal = (value) => {
    this.setState({
      laboursTotalHasManualOverride: true,
      manualOverrides: {
        ...this.state.manualOverrides,
        manualLaboursTotal: value,
      },
    });
  };

  onResetLabourTotalOverride = () => {
    this.setState({
      laboursTotalHasManualOverride: false,
      manualOverrides: {
        ...this.state.manualOverrides,
        manualLaboursTotal: 0,
      },
    });
  };

  onChangePartsTotal = (value) => {
    this.setState({
      partsTotalHasManualOverride: true,
      manualOverrides: {
        ...this.state.manualOverrides,
        manualPartsTotal: value,
      },
    });
  };

  onChangeWarrantySavings = (value) => {
    this.setState({
      initialFormValues: {
        ...this.state.initialFormValues,
        warrantySavings: value,
      },
    });
  };

  onResetPartsTotalOverride = () => {
    this.setState({
      partsTotalHasManualOverride: false,
      manualOverrides: {
        ...this.state.manualOverrides,
        manualPartsTotal: 0,
      },
    });
  };

  onClickImage = async (imageUrlOrBase64) => {
    this.setState({
      isTiff: false,
      previewImageUrls: [],
      currentPreviewIndex: 0,
    });
    // let auxImageUrl = imageUrlOrBase64;
    // // check if image is actually a URL
    // if (!(imageUrlOrBase64.includes('http://') || imageUrlOrBase64.includes('https://'))) {
    //   // upload the image to the server to get the URL
    //   AppStore.addLoading('Uploading Image');
    //   // convert imageUrlOrBase64 to a file
    //   const base64ToFile = (base64, filename) => {
    //     const arr = base64.split(',');
    //     const mime = arr[0].match(/:(.*?);/)[1];
    //     const bstr = atob(arr[1]);
    //     let n = bstr.length;
    //     const u8arr = new Uint8Array(n);
    //     while (n--) {
    //       u8arr[n] = bstr.charCodeAt(n);
    //     }
    //     return new File([u8arr], filename, { type: mime });
    //   };
    //   const fileExtension = imageUrlOrBase64.split(';')[0].split('/')[1];
    //   const randomName = Math.random().toString(36).substring(7);
    //   const file = base64ToFile(imageUrlOrBase64, `preview_${randomName}.${fileExtension}`);
    //   const fd = new FormData();
    //   fd.append('singleInputFileName', file);
    //   const data = await webServiceProvider.postFormData(
    //     'dashboard/v1/uploadShopImage?shopId=' + ShopStore.currentShop.id,
    //     fd
    //   );
    //   auxImageUrl = data.fileUrl;
    //   AppStore.removeLoading('Uploading Image');
    // }
    // check if isMobile
    if (this.state.isMobileScreenSize) {
      this.setState(
        {
          currentImagePreviewSrc: imageUrlOrBase64,
          isImagePreviewModalVisible: true,
        },
        this.scrollToTopAndSaveOriginalMediaPreviewData
      );
    } else {
      // show on split screen
      this.setState(
        {
          scannedWorkOrderPreview: imageUrlOrBase64,
        },
        this.scrollToTopAndSaveOriginalMediaPreviewData
      );
    }
  };

  // Add method to track form changes
  handleFormChange = (changedData = null) => {
    if (!this.state.hasUnsavedChanges) {
      this.setState({ hasUnsavedChanges: true }, () => {
        if (this.hasUnsavedChangesRef?.current !== undefined) {
          this.hasUnsavedChangesRef.current = true;
        }
      });
    }
    if (changedData && typeof changedData === 'object') {
      // if changedData was provided and it's an object,
      // merge it with the current workOrderFormData
      // it'll be used later when saving the work order
      this.setState({
        workOrderFormData: {
          ...this.state.workOrderFormData,
          ...changedData,
        },
      });
    }
  };

  mainColLgSpan = () => {
    // If in slide-out mode and not on mobile, use full width to remove horizontal padding
    if (this.props.isSlideOutMode && !this.state.isMobileScreenSize) {
      return 24;
    }
    return this.state.importedInformation || this.state.scannedWorkOrderPreview
      ? 22
      : 18;
  };

  handleFormDataChange = (formData, isInitialLoad = false) => {
    this.setState({
      workOrderFormData: formData,
      hasUnsavedChanges: isInitialLoad ? false : true,
    });
  };

  handleWODetailsStateChange = ({ loadingCarData, isShopDataReady }) => {
    this.setState({ loadingCarData, isShopDataReady });
  };

  handleScanFromOptional = (files) => {
    if (!files || files.length === 0) return;
    this.setState({
      scanQueue: files,
      isUploadWorkOrderModalVisible: true,
      isErrorWorkOrderUpload: false,
    });
  };

  render() {
    const {
      showUploadButton,
      isUploadWorkOrderModalVisible,
      uploading,
      // progress,
    } = this.state;

    const workOrderID =
      this.state.initialFormValues.woInvoiceNumber ||
      this.state.currentWorkOrder?.id;

    /**
     * Please check: https://www.notion.so/Dashboard-Work-Order-Context-Management-63b5031b223541f9b1b3c2f0c6527f53?pvs=4
     * For more information about the context values and how it's being used
     */
    const contextValue = {
      state: this.state,
      shopUsers: this.shopUsers,
      shopParts: this.state.shopParts,
      shopVendors: this.state.shopVendors,
      loadingVendors: this.state.loadingVendors,
      currentCarIssues: this.state.currentCarIssues,
      onCarSelect: this.onCarSelect,
      onChangeAssignedTo: this.onChangeAssignedTo,
      onChangePurchaseOrder: this.onChangePurchaseOrder,
      onGetCarDetails: this.onGetCarDetails,
      vendorOptions: this.vendorOptions,
      updateWorkOrder: this.updateWorkOrder,
      onClickAddPurchaseOrder: this.onClickAddPurchaseOrder,
      onLocationSelect: this.onLocationSelect,
      handleFormChange: this.handleFormChange,
      // line items
      onSelectAsmCode: this.onSelectAsmCode,
      onSelectSystemCode: this.onSelectSystemCode,
      onAddLineItem: this.onAddLineItem,
      updateLineItemHeader: this.updateLineItemHeader,
      onRemoveLineItem: this.onRemoveLineItem,
      onChangeLineItemField: this.onChangeLineItemField,
      onAddNewPart: this.onAddNewPart,
      onUpdateLaborItem: this.onUpdateLaborItem,
      onUpdatePartItem: this.onUpdatePartItem,
      onUpdateMiscellaneousItem: this.onUpdateMiscellaneousItem,
      onUpdateDefectItem: this.onUpdateDefectItem,
      onUpdateServiceItem: this.onUpdateServiceItem,
      onChangeCollapseItems: this.onChangeCollapseItems,
      addSubItem: this.addSubItem,
      moveItem: this.moveItem,
      deleteSubItem: this.deleteSubItem,
      changeSubItem: this.changeSubItem,
      addNewLineItem: this.addNewLineItem,
      deleteLineItem: this.deleteLineItem,
      updateVMRSCode: this.updateVMRSCode,
      removeInspectionIssue: this.removeInspectionIssue,
      toggleActive: this.toggleActive,
      changeItemProperty: this.changeItemProperty,
      onAddLabor: this.onAddLabor,
      onAddPart: this.onAddPart,
      onAddIssue: this.onAddIssue,
      onAddMiscellaneous: this.onAddMiscellaneous,
      onAddDefect: this.onAddDefect,
      onRemoveLabor: this.onRemoveLabor,
      onRemovePart: this.onRemovePart,
      onRemoveMiscellaneous: this.onRemoveMiscellaneous,
      onRemoveDefect: this.onRemoveDefect,
      onRemoveIssue: this.onRemoveIssue,
      // optional informations
      setImageUploaderContainerRef: this.setImageUploaderContainerRef,
      setDocumentUploaderContainerRef: this.setDocumentUploaderContainerRef,
      imageUploaderContainerRef: this.imageUploaderContainerRef,
      documentUploaderContainerRef: this.documentUploaderContainerRef,
      handleImageUpload: this.handleImageUpload,
      handleFileUpload: this.handleFileUpload,
      setInitialFormValue: this.setInitialFormValue,
      setCurrentWorkOrderValue: this.setCurrentWorkOrderValue,
      // summary
      onChangeTaxType: this.onChangeTaxType,
      onChangeTaxValue: this.onChangeTaxValue,
      onChangeSecondTaxType: this.onChangeSecondTaxType,
      onChangeSecondTaxValue: this.onChangeSecondTaxValue,
      onChangeMiscType: this.onChangeMiscType,
      onChangeMiscValue: this.onChangeMiscValue,
      onChangeSummaryTotal: this.onChangeSummaryTotal,
      onChangeLabourTotal: this.onChangeLabourTotal,
      onChangePartsTotal: this.onChangePartsTotal,
      onChangeWarrantySavings: this.onChangeWarrantySavings,
      onResetCostsTotalOverride: this.onResetCostsTotalOverride,
      onResetLabourTotalOverride: this.onResetLabourTotalOverride,
      onResetPartsTotalOverride: this.onResetPartsTotalOverride,
      mainColLgSpan: this.mainColLgSpan,
      // images and documents
      onClickImage: this.onClickImage,
    };

    const { loadingWorkOrder, loadingInitial, isShopDataReady } = this.state;
    const showSpinner = loadingWorkOrder || loadingInitial || !isShopDataReady;

    return (
      <WorkOrderProvider value={contextValue} key={workOrderID}>
        {showSpinner && (
          <LoadingContainer>
            <Spin />
          </LoadingContainer>
        )}
        <div
          className="wrapper-wo"
          style={{ display: showSpinner ? 'none' : 'block' }}
        >
          {!this.props.isSlideOutMode && <DealershipChooser />}
          <Row gutter={[8, 8]}>
            <Col xs={24} sm={24}>
              <FlexRow>
                {!this.props.isSlideOutMode && (
                  <h1
                    style={{
                      flex: 1,
                    }}
                    className="page-title"
                  >
                    {this.state.title} Work Order
                  </h1>
                )}
                <>
                  {(showUploadButton || this.state.initialFileScanning) &&
                    !this.props.isSlideOutMode && (
                      <UploadButton
                        icon={<UploadOutlined />}
                        onClick={() => {
                          this.setState(
                            {
                              isUploadWorkOrderModalVisible: true,
                              isErrorWorkOrderUpload: false,
                            },
                            () => {
                              this.forceUpdateTabs(); // Force re-render after setting the state
                            }
                          );
                        }}
                      >
                        Scan Work Order
                      </UploadButton>
                    )}
                  {this.state.currentWorkOrder && (
                    <>
                      <Button
                        type="primary"
                        ghost
                        onClick={() => this.handleExportClick('download')}
                        className="small-screen"
                        style={{ marginLeft: 10 }}
                      >
                        <DownloadOutlined />
                        Download
                      </Button>
                      <Button
                        type="primary"
                        ghost
                        onClick={() => this.handleExportClick('print')}
                        className="small-screen"
                        style={{ marginLeft: 10 }}
                      >
                        <PrinterOutlined />
                        Print
                      </Button>
                    </>
                  )}
                </>
              </FlexRow>
              <UploadImageModal
                isVisible={isUploadWorkOrderModalVisible}
                isLoading={this.state.isLoadingWorkOrderUpload}
                isError={this.state.isErrorWorkOrderUpload}
                onUpload={(file) => {
                  const scanQueue = file ? [file] : this.state.scanQueue || [];
                  if (scanQueue.length > 0)
                    this.handleWorkOrderUpload(scanQueue);
                }}
                onCancel={() => {
                  // stop the upload if it's still uploading
                  if (this.state.isLoadingWorkOrderUpload) {
                    if (this.state.workOrderImgOcrSignal) {
                      this.state.workOrderImgOcrSignal.abort();
                    }
                    this.setState(
                      (prevState) => ({
                        isUploadCancelled: true,
                        isLoadingWorkOrderUpload: false,
                        isErrorWorkOrderUpload: false,
                        // progress: 0,
                        currentStep: 0,
                        scanDone: false,
                        scanQueue: [],
                        uploading: false,
                        workOrderImgOcrSignal: null,
                        uploadedImages:
                          prevState.currentUploadingFile?.type?.includes(
                            'image'
                          )
                            ? (prevState.uploadedImages || []).filter(
                                (img) =>
                                  img.file.uid !==
                                  prevState.currentUploadingFile.uid
                              )
                            : prevState.uploadedImages || [],
                        uploadedFiles:
                          prevState.currentUploadingFile?.type?.includes(
                            'application'
                          )
                            ? (prevState.uploadedFiles || []).filter(
                                (file) =>
                                  file.file.uid !==
                                  prevState.currentUploadingFile.uid
                              )
                            : prevState.uploadedFiles || [],
                        scannedWorkOrderPreview: null,
                        currentUploadingFile: null,
                      }),
                      () => {
                        const uuidPattern = new RegExp(
                          /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/
                        );
                        if (
                          this.imageUploaderContainerRef &&
                          this.imageUploaderContainerRef.setFileList
                        ) {
                          this.imageUploaderContainerRef.setFileList([
                            ...(this.state.uploadedImages || []).map(
                              (img) => img.file
                            ),
                            ...(this.state.wo_images || []).map((url) => ({
                              url,
                              status: 'done',
                              thumbUrl: url,
                            })),
                          ]);
                        }
                        if (
                          this.documentUploaderContainerRef &&
                          this.documentUploaderContainerRef.setFileList
                        ) {
                          this.documentUploaderContainerRef.setFileList([
                            ...(this.state.uploadedFiles || []).map((file) => {
                              let url;
                              try {
                                url = URL.createObjectURL(file.file);
                              } catch (error) {
                                url = file.file.url;
                              }
                              return {
                                uid: file.file.uid,
                                name: file.file.name,
                                status: 'done',
                                url,
                                type: file.file.type,
                                size: file.file.size,
                              };
                            }),
                            ...(this.state.wo_documents || []).map((url) => ({
                              url,
                              status: 'done',
                              name: url
                                .split('/')
                                .pop()
                                .replace(uuidPattern, ''),
                            })),
                          ]);
                        }
                      }
                    );
                  }
                  this.setState({
                    isUploadWorkOrderModalVisible: false,
                  });
                }}
                uploading={uploading}
                // progress={progress}
                currentStep={this.state.currentStep}
                initialFile={
                  this.state.scanQueue?.length > 0 ? this.state.scanQueue : []
                }
              />
            </Col>
          </Row>

          {/* If mobile, render the tabs structure */}
          {this.state.isMobileScreenSize && (
            <Row className="limit-size-tablets">
              <Col span={24}>
                <Tabs
                  className="scrollable-tabs"
                  defaultActiveKey={this.state.activeTab}
                  onChange={(key) => {
                    this.setState({ activeTab: key }, () => {
                      this.props.history.push({
                        search: `?tab=${this.state.activeTab}`,
                      });
                    });
                  }}
                >
                  <Tabs.TabPane
                    tab="Work Order Details"
                    key="work-order-details"
                  >
                    <Row type="flex" justify="center">
                      <Col sm={24} lg={18}>
                        <WorkOrderForm
                          onFormDataChange={this.handleFormDataChange}
                          childRef={(ref) => (this.workOrderFormRef = ref)}
                          handleWODetailsStateChange={
                            this.handleWODetailsStateChange
                          }
                        />
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab="Work Order Lines"
                    key="work-order-line-items"
                    disabled={!this.state.currentWorkOrder}
                  >
                    <Row type="flex" justify="center">
                      <Col sm={24} lg={18}>
                        <WorkOrderLineItems />
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab="Optional Information"
                    key="optional-information"
                    disabled={!this.state.currentWorkOrder}
                  >
                    <Row type="flex" justify="center">
                      <Col className="pb-15" sm={24} lg={18}>
                        <WorkOrderOptionalInformation
                          forScan={this.handleScanFromOptional}
                        />
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                  <Tabs.TabPane
                    tab="Summary"
                    key="summary"
                    disabled={!this.state.currentWorkOrder}
                  >
                    <Row type="flex" justify="center">
                      <Col sm={24} lg={18}>
                        <WorkOrderCostSummary
                          refs={(ref) => (this.costSummaryRef = ref)}
                        />
                      </Col>
                    </Row>
                  </Tabs.TabPane>
                </Tabs>
              </Col>
            </Row>
          )}

          {/* If desktop, render everything in one page */}
          {!this.state.isMobileScreenSize && (
            <Row>
              {this.state.importedInformation ||
              this.state.scannedWorkOrderPreview ||
              this.state.loadingPreview ? (
                <Col span={10}>
                  <Spin
                    spinning={this.state.loadingPreview}
                    tip="Loading preview..."
                  ></Spin>
                  {this.state.importedInformation && (
                    <>
                      <NotificationBanner
                        message="Double-check all data fields are correct after scanning the work order to avoid errors and ensure smooth operations."
                        onClose={() => {}}
                      />
                      <Collapse
                        style={{ marginBottom: 15, marginTop: 15 }}
                        defaultActiveKey={[]}
                        onChange={() => {}}
                      >
                        <Panel header="Imported Information" key="1">
                          <ImportedInformation>
                            <pre>{this.state.importedInformation}</pre>
                          </ImportedInformation>
                        </Panel>
                      </Collapse>
                    </>
                  )}
                  {this.state.scannedWorkOrderPreview && (
                    <MediaPreview
                      loading={this.state.loadingPreview}
                      media={this.state.scannedWorkOrderPreview}
                      isTiff={this.state.isTiff}
                      tiffUrls={this.state.previewImageUrls}
                      currentTiffIndex={this.state.currentPreviewIndex}
                      isFixed={this.state.isMediaPreviewFixed}
                      fixedWidth={this.originalMediaPreviewWidth}
                      fixedLeft={this.originalMediaPreviewLeft}
                      workOrderId={this.state.workOrder?.id?.toString()}
                      isSlideOutMode={this.props.isSlideOutMode}
                      onNavigatePage={(direction) => {
                        this.setState((prevState) => {
                          const newIndex =
                            direction === 'prev'
                              ? Math.max(prevState.currentPreviewIndex - 1, 0)
                              : Math.min(
                                  prevState.currentPreviewIndex + 1,
                                  prevState.previewImageUrls?.length - 1
                                );
                          return { currentPreviewIndex: newIndex };
                        });
                      }}
                    />
                  )}
                </Col>
              ) : null}
              <Col
                span={
                  this.state.importedInformation ||
                  this.state.scannedWorkOrderPreview
                    ? 14
                    : 24
                }
              >
                <Row type="flex" justify="center">
                  <Col sm={24} lg={this.mainColLgSpan()}>
                    <WorkOrderForm
                      onFormDataChange={this.handleFormDataChange}
                      childRef={(ref) => (this.workOrderFormRef = ref)}
                      handleWODetailsStateChange={
                        this.handleWODetailsStateChange
                      }
                    />
                  </Col>
                </Row>
                {/* <Row type="flex" justify="center">
                <Col sm={24} lg={18}>
                  <WorkOrderLineItems />
                </Col>
              </Row> */}
                <Row type="flex" justify="center">
                  <Col sm={24} lg={this.mainColLgSpan()}>
                    <LineItems />
                  </Col>
                </Row>
                <Row type="flex" justify="center">
                  <Col className="pb-15" sm={24} lg={this.mainColLgSpan()}>
                    <WorkOrderOptionalInformation
                      forScan={this.handleScanFromOptional}
                      wo_documents={this.state.wo_documents}
                      uploadedFiles={this.state.uploadedFiles}
                    />
                  </Col>
                </Row>
                <Row type="flex" justify="center">
                  <Col sm={24} lg={this.mainColLgSpan()}>
                    <WorkOrderCostSummary
                      isEdit={this.state.isEdit}
                      ref={(ref) => (this.costSummaryRef = ref)}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          )}

          <Row gutter={[8, 8]}>
            <Col span={18} offset={3}>
              <ActionButtonsWrapperFlexEnd>
                <PitstopButton
                  type="link"
                  onClick={this.goBack}
                  data-testid="work-order-cancel-button"
                >
                  Cancel
                </PitstopButton>
                {!this.state.isMobileScreenSize ? (
                  <PitstopButton
                    loading={this.state.savingWorkOrder}
                    onClick={() => this.onClickSave('exit')}
                    disabled={this.state.isDisabled}
                    data-testid="work-order-save-exit-button"
                  >
                    {this.props.isSlideOutMode ? 'Save' : 'Save & Exit'}
                  </PitstopButton>
                ) : (
                  <>
                    {this.state.activeTab !== 'summary' && (
                      <PitstopButton
                        onClick={() => this.onClickSave('exit')}
                        type="link"
                        disabled={this.state.isDisabled}
                      >
                        {this.props.isSlideOutMode ? 'Save' : 'Save & Exit'}
                      </PitstopButton>
                    )}
                    <PitstopButton
                      loading={this.state.savingWorkOrder}
                      onClick={this.onClickSave}
                      disabled={this.state.isDisabled}
                    >
                      {this.state.activeTab === 'summary'
                        ? 'Finish'
                        : 'Next Section'}
                    </PitstopButton>
                  </>
                )}
              </ActionButtonsWrapperFlexEnd>
            </Col>
          </Row>

          <StyledModal
            id={ADD_PART_MODAL}
            title={'Add New Part'}
            width={640}
            okText="Save"
            footer={
              <>
                <Button
                  key="back"
                  onClick={() => {
                    AppStore.openModals.set(ADD_PART_MODAL, false);
                  }}
                  data-testid="add-part-cancel-button"
                >
                  Cancel
                </Button>
                <Button
                  key="submit"
                  type="primary"
                  onClick={() => {
                    this.onSubmitAddPartModal();
                  }}
                  loading={this.state.loadingSavingNewPart}
                  data-testid="add-part-save-button"
                >
                  Save
                </Button>
              </>
            }
          >
            <Row gutter={[8, 8]}>
              <Col span={24}>
                <Text strong>Part Number</Text>
                <Input
                  style={{ width: '100%' }}
                  value={this.state.newPart.number}
                  onChange={(e) => {
                    this.setState({
                      newPart: {
                        ...this.state.newPart,
                        number: e.target.value,
                      },
                    });
                  }}
                  data-testid="add-part-number-input"
                />
                {String(this.state.newPart.number).trim() &&
                  this.state.shopParts.find(
                    (shopPart) =>
                      String(shopPart.number).trim() ===
                      String(this.state.newPart.number).trim()
                  ) && (
                    <Typography.Text type="danger">
                      A part with this number already exists in your inventory.
                      To avoid duplicates, increase its stock count instead of
                      adding a new part.{' '}
                      <Link
                        to={`/parts/${
                          this.state.shopParts.find(
                            (shopPart) =>
                              String(shopPart.number).trim() ===
                              String(this.state.newPart.number).trim()
                          ).id
                        }`}
                      >
                        Go to Existing Part
                      </Link>
                    </Typography.Text>
                  )}
              </Col>
              <Col span={24}>
                <Text strong>Description</Text>
                <TextArea
                  id="description"
                  rows={4}
                  placeholder="Add Description"
                  value={this.state.newPart.description}
                  onChange={(e) => {
                    this.setState({
                      newPart: {
                        ...this.state.newPart,
                        description: e.target.value,
                      },
                    });
                  }}
                  data-testid="add-part-description-input"
                />
              </Col>
              <Col span={24}>
                <Text strong>Unit Cost:</Text>
                <InputNumber
                  formatter={(value) =>
                    `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                  }
                  parser={(value) => value.replace(/\$\s?|(,*)/g, '')}
                  style={{ width: '100%' }}
                  value={this.state.newPart.cost}
                  onChange={(value) => {
                    this.setState({
                      newPart: {
                        ...this.state.newPart,
                        cost: value,
                      },
                    });
                  }}
                  inputMode="tel"
                  data-testid="add-part-unit-cost-input"
                />
              </Col>
              <Col span={24}>
                <Checkbox
                  style={{
                    marginTop: '10px',
                  }}
                  onChange={(e) => {
                    this.setState({
                      newPart: {
                        ...this.state.newPart,
                        addToInventory: e.target.checked,
                      },
                    });
                  }}
                  data-testid="add-part-to-inventory-checkbox"
                >
                  Check box to add part to Parts List inventory. Leave unchecked
                  to exclude from inventory
                </Checkbox>
              </Col>
            </Row>
          </StyledModal>

          <ImagePreviewModal
            open={this.state.isImagePreviewModalVisible}
            footer={null}
            onCancel={() => {
              this.setState({
                isImagePreviewModalVisible: false,
                currentImagePreviewSrc: '',
              });
            }}
          >
            <MediaPreview
              media={this.state.currentImagePreviewSrc}
              workOrderId={this.state.workOrder?.id?.toString()}
            />
          </ImagePreviewModal>

          <PurchaseOrderAddModal
            visible={this.state.addPOModalVisible}
            onSave={this.onAddPurchaseOrder}
            onCancel={() => {
              this.setState({
                addPOModalVisible: false,
              });
            }}
          />
        </div>
      </WorkOrderProvider>
    );
  }
}

export default withRouter(observer(WorkOrderDetails));
